<template>
    <div id="planbord" :style="[gridDefinitionData]" :class="{compact: viewOptions.showCompactMode, }">
        <EnvironmentBadge :environment="globals.environment" v-if="globals" />
        <OptionsRow
            v-if="optionsRowDataLoaded && viewableRosters.length > 0"
            :roster="roster"
            :preferences="preferences"
            :dateInputRange="selectableDateRange"
            :modelValue="rosterViewSettings"
            @update:modelValue="updateViewSettings"
            @update:preference="updatePreference"
            :rosters="viewableRosters"
        />
        <template v-if="!!roster && shiftsLoaded && globals && organisation">
            <FilterBar
                @update:viewOptions="updateViewOptions"
                @update:preference="updatePreference"
                @update:showPlusMinusBalancesFrom="updatePlusMinusBalancesShownFrom"
                @update:showDeploymentCounterFrom="updateDeploymentCounterFrom"
                @update:enabledBalanceFilters="updateEnabledBalanceFilters"
                @reload:roster="reloadPlanbord"
                @rosterProposed="rosterProposed"
                @rosterPublished="reloadPlanbord"
                @legend:show="showLegend = true"
                @open:themeSettings="showThemeSettings = true"
                :viewOptions="viewOptions"
                :globals="globals"
                :showPlusMinusBalanceFrom="showPlusMinusBalanceFrom"
                :showDeploymentCounterFrom="showDeploymentCounterFrom"
                :rosterViewSettings="rosterViewSettings"
                :roster="roster"
                :preferences="preferences"
                :permissions="permissionsForSelectedRoster"
                :enabledBalanceFilters="enabledBalanceFilters"
                :leaveOverviews="leaveOverviews"
                :deploymentCountConfigurations="deploymentCountConfigurations"
                :areWorkSchedulesEnabled="areWorkSchedulesEnabled"
                :allowRosterActionsToAnyWeekDay="allowRosterActionsToAnyWeekDay"
            />
            <PlanbordRow
                ref="row"
                :viewOptions="viewOptions"
                :rosterNames="rosterNames"
                :roster="roster"
                :enabledCompanies="enabledCompanies"
                :budgetDayCounters="budgetDayCounters"
                :preferences="preferences"
                :leaveOverviews="leaveOverviews"
                :deploymentCountConfigurations="deploymentCountConfigurations"
                :deploymentCounts="deploymentCounts"
                :organisation="organisation"
                :contractDeviationLabels="globals.contractCaptions"
                :shifts="shifts"
                :permissions="permissionsPerRoster"
                :enabledBalanceAndCountDisplays="enabledBalanceAndCountDisplays"
                :enabledWorksSchedule="canEditWorkSchedules"
                :resourceFunctionNames="resourceFunctionNames"
                :rosterViewSettings="rosterViewSettings"
                :informationColumns="informationColumns"
                :dayColumns="dayColumns"
                :workSchedulePositionsPerDay="workSchedulePositionsPerDay"
                @create:deployment="createDeployment"
                @update:deployment="updateDeployment"
                @delete:deployment="deleteDeployment"
                @create:stampShift="createStampShift"
                @move:resourceRosterRank="batchUpdateRosterRanks"
                @move:rosterTitleRank="batchUpdateRosterRanks"
                @open:workSchedule="openWorkSchedule"
                @open:addOpenShiftMarketplaceEmployee="openCompanyEmployeeSelectModal"
            />
            <WorkSchedule
                v-if="canEditWorkSchedules"
                :fullRoster="roster"
                :organisation="organisation"
                :shifts="shifts"
                :rosterNames="rosterNames"
                :enabledCompanies="enabledCompanies"
                @update:workSchedules="updateWorkSchedules"
            />
            <DeploymentCellContextMenu
                :organisation="organisation"
                :permissions="permissionsForSelectedRoster"
                :roster="roster"
                :shifts="shifts"
                :defaultShiftIdPerRoster="defaultShiftIdPerRoster"
                :hrCouplingEnabled="hrCouplingEnabled"
                :oldHrCouplingEnabled="oldHrCouplingEnabled"
                :hrCouplingType="globals?.hrCouplingType ?? undefined"
                @delete:leavePeriod="deleteLeavePeriod"
                @delete:sickPeriod="deleteSickPeriod"
                @reload:roster="reloadPlanbord"
                @reload:deployments="reloadResource"
                @update:deployments="updateDeployments"
                @create:stampShifts="createPersonalStampShifts"
            />
            <Tooltip
                :rosters="rosters"
                :rosterId="rosterViewSettings.rosterId"
                :organisationDeploymentStates="organisation.organisationDeploymentStates"
                :organisationDeploymentTypes="organisation.organisationDeploymentTypes"
                :allowVisibility="!viewOptions.hideTooltip"
                :shifts="shifts"
                :projects="projects"
                :positionAssignmentGroups="rosterPositionAssignmentGroups"
            />
            <LeavePeriodModal :proposedUntil="rosterProposedUntil" :preferences="preferences" @update:preference="updatePreference" @reload:resource="reloadResource" />
            <ShortcutModal :organisationDeploymentStates="organisation.organisationDeploymentStates" :rosterDays="roster.days" :shortcutPreferences="shortcutPreferences" @change:shortcuts="changeShortcutPreferences" />
            <LegendModal
                :showModal="showLegend"
                :viewOptions="viewOptions"
                :globals="globals"
                :organisations="organisations"
                :permissions="permissionsForSelectedRoster"
                :enabledFeatures="enabledFeatures"
                @closeModal="showLegend = false"
            />
            <RosterCommentModal
                :rosterNames="rosterNames"
                :currentRosterId="roster.rosterId"
                @save:comment="saveRosterComment"
                @delete:comment="deleteRosterComment"
            />
            <RosterRankSetsModal
                :roster="roster"
                @update:rosterRankSets="reloadRosterResourceRanksAndTitles"
            />
            <EmploymentCommentModal
                @save:employmentComment="saveEmploymentComment"
            />
            <KeyboardDeploymentAutocomplete
                :organisation="organisation"
                :shifts="shifts"
                :shiftIds="shiftIdsOnCurrentRoster"
                :shortcutPreferences="shortcutPreferences"
                @delete:cells="deleteDeploymentsAndStampShifts"
                @create:deployment="createDeployment"
                @update:deployment="updateDeployment"
                @delete:deployment="deleteDeployment"
                @create:deploymentProposal="createDeploymentProposal"
                @update:deploymentProposal="updateDeploymentProposal"
                @delete:deploymentProposal="deleteDeploymentProposal"
                @save:deployments="createOrUpdateDeployments"
                @create:stampShift="createStampShift"
                @create:stampShifts="createPersonalStampShifts"
                @update:stampShift="updatePersonalStampShift"
                @delete:stampShift="deletePersonalStampShift"
            />
            <ShiftStateUpdateModal
                :rosterId="roster.rosterId"
                :rosters="rosters"
                :permissionsPerRoster="permissionsPerRoster"
                :organisations="organisations"
                :shifts="shifts"
                :shiftIdsPerRoster="shiftIdsPerRoster"
                :defaultShiftIdPerRoster="defaultShiftIdPerRoster"
                :projects="projects"
                :leaveKinds="leaveKinds"
                @create:deployment="createDeployment"
                @update:deployment="updateDeployment"
                @delete:deployment="deleteDeployment"
                @create:deploymentProposal="createDeploymentProposal"
                @update:deploymentProposal="updateDeploymentProposal"
                @delete:deploymentProposal="deleteDeploymentProposal"
            />
            <StampShiftEditModal
                :rosterId="roster.rosterId"
                :rosters="rosters"
                :permissionsPerRoster="permissionsPerRoster"
                :shifts="shifts"
                :shiftIdsPerRoster="shiftIdsPerRoster"
                :defaultShiftIdPerRoster="defaultShiftIdPerRoster"
                @create:stampShift="createStampShift"
                @update:stampShift="updatePersonalStampShift"
                @delete:stampShift="deletePersonalStampShift"
            />
            <Wishes
                :shifts="shifts"
                :shiftIds="shiftIdsOnCurrentRoster"
                @create:wish="createWish"
                @update:wish="updateWish"
                @delete:wish="deleteWish"
            /><!-- fill with shift ids that are on the roster -->
            <SickPeriodEditModal
                @create:sickPeriod="createSickPeriod"
                @update:sickPeriod="updateSickPeriod"
            />
            <DeploymentsStampsDeleteModal
                @delete:stamp="reloadResourceStampData"
                @reload:roster="reloadPlanbord"
                @reload:deployments="reloadResource"
                @reload:stamps="reloadResourceStampData"
            />
            <AssignedDaysModal
                @update:assignedDay="updateAssignedDay"
                @delete:assignedDay="deleteAssignedDay"
                @update:stampAssignedDay="updateStampAssignedDay"
                @delete:stampAssignedDay="deleteStampAssignedDay"
            />
            <StampDeployModal
                :roster="roster"
                @reload:roster="reloadPlanbord"
            />
            <DeploymentHistoryModal
                :shifts="shifts"
                :organisationDeploymentStates="organisation.organisationDeploymentStates"
                :organisationDeploymentTypes="organisation.organisationDeploymentTypes"
                :projects="projects"
                :defaultShiftIdPerRoster="defaultShiftIdPerRoster"
                :shiftIdsOnCurrentRoster="shiftIdsOnCurrentRoster"
                :rosterNames="rosterNames"
                @restore:deployments="restoreDeployments"
            />
            <ThemeSettings
                :showModal="showThemeSettings"
                :preferences="preferences"
                :areWorkSchedulesEnabled="areWorkSchedulesEnabled"
                @update:preference="updatePreference"
                @closeModal="showThemeSettings = false"
            />
            <DeploymentProposalAcceptModal
                :shifts="shifts"
                :organisations="organisations"
                :rosters="rosters"
                :leaveKinds="leaveKinds"
                :projects="projects"
                @updateLocal:deploymentProposal="saveDeploymentProposalInLocalRoster"
            />
        </template>
        <Modals @reload:stamp="reloadResourceStampData" />
        <!-- Modals that don't depend on data (e.g. load it themself) -->
        <Loader :rosterName="currentRosterName" />
        <ResourceDetailsModal :rosterNames="rosterNames" />
        <CompanyEmployeeSelectModal @addEmployeeToCompany="addEmployeeToCompany" />
        <TitleEditModal
            :roster="roster"
            @create:title="reloadRosterResourceRanksAndTitles"
            @update:title="updateRosterTitle"
            @delete:title="deleteRosterTitle"
        />
        <AskTenantModal
            :showModal="askTenantId"
            v-model="globalTenant"
            @confirmAction="applyTenant"
        />
        <AppFooter />
    </div>
</template>

<script lang="ts">
    import AppFooter from '@/components/AppFooter.vue';
    import PlanbordRow from '@/components/Planbord/PlanbordRow.vue';
    import FilterBar from '@/components/FilterBar/FilterBar.vue';
    import EnvironmentBadge from '@/components/Planbord/EnvironmentBadge.vue';
    import OptionsRow from '@/components/Planbord/OptionsRow.vue';
    import Modals from '@/components/Modals.vue';
    import Loader from '@/components/modals/Loader.vue';
    import Notify from '@/components/modals/Notify.vue';
    import WorkSchedule from '@/components/WorkSchedule.vue';
    import CellIntoViewScroller from '@/util/CellIntoViewScroller';
    import {RosterData, RosterSelectOption, ViewOptions, BudgetDayCounters, PlanbordGridRow, CellCoordinates, RosterOptionData, PermissionsForRoster, NullableRange, RosterDay, PlanbordQueryParameters} from '@/types/types';
    import {PlanbordQueryFilter} from '@/util/PlanbordQueryFilter';
    import KeyboardDeploymentAutocomplete from '@/components/deploymentAutocomplete/KeyboardDeploymentAutocomplete.vue';
    import {NavigationDirection} from '@/types/enums/NavigationDirection';
    import {
        ApiClient,
        PersonalStamp,
        PersonalStampShift,
        Roster,
        RosterUser,
        Company,
        Organisation,
        Globals,
        LeaveOverview,
        Shift,
        PersonalPlanningWish,
        Resource,
        EmploymentComment,
        Deployment,
        LeavePeriod,
        DeploymentBatchSaveIds,
        AssignedDay,
        SickPeriod,
        Project,
        FeatureToggle,
        DeploymentHistory,
        DeploymentCountConfiguration,
        DeploymentCountConfigurationBalances,
        Title,
        OpenShift,
        PositionAssignmentSetRoster,
        PositionAssignmentSetGroup,
        RosterComment as RosterCommentModel,
        RosterDayDeploymentCountConfigurationBalances,
        PersonalStampAssignedDay,
        ResourceFunction,
        PersonalStampBatchSaveIds,
        WorkSchedulePosition,
        DeploymentProposal,
        LeaveKind,
        RosterRankSet,
        OpenShiftState,
        RosterTypeNumber,
        LeavePeriodType,
        WorkScheduleFulfillmentRequirement,
        OAuthToken,
        ApiError,
        HttpStatusCode,
    } from '@cquential/api';
    import {RosterViewSettings, MoveResourceRankData, MoveTitleRankData} from '@/types';
    import ShiftStateUpdateModal from '@/components/modals/ShiftStateUpdateModal.vue';
    import StampShiftEditModal from '@/components/modals/StampShiftEditModal.vue';
    import ResourceDetailsModal from '@/components/modals/ResourceDetailsModal.vue';
    import TitleEditModal from '@/components/modals/TitleEditModal.vue';
    import Tooltip from '@/components/Tooltip/Tooltip.vue';
    import RosterLoader from '@/util/RosterLoader';
    import LeavePeriodModal from '@/components/modals/LeavePeriodModal.vue';
    import DeploymentCellContextMenu from '@/components/Planbord/DeploymentCellContextMenu.vue';
    import ShortcutModal from '@/components/modals/ShortcutModal.vue';
    import RosterCommentModal from '@/components/modals/RosterCommentModal.vue';
    import EmploymentCommentModal from '@/components/modals/EmploymentCommentModal.vue';
    import Wishes from '@/components/modals/Wishes.vue';
    import DeploymentHistoryModal from '@/components/modals/DeploymentHistoryModal.vue';
    import {PlanbordResource, ResourceWithKeyedByDateObjects, PlanbordLeavePeriod, CompanyEmployeeWithDeployments, CompanyWithDeploymentsAndEmployees, PlanbordWorkSchedulePosition} from '@/types/api-object-extensions';
    import WarningInformationModal from '@/components/modals/WarningInformationModal.vue';
    import LeavePeriodHandler from '@/util/leaveperiodhandler';
    import cloneDeep from 'lodash/cloneDeep';
    import RosterConverter from '@/util/rosterconverter';
    import AssignedDaysModal from '@/components/modals/AssignedDaysModal.vue';
    import CustomDaysVisibleModal from '@/components/modals/CustomDaysVisibleModal.vue';
    import SickPeriodEditModal from '@/components/modals/SickPeriodEditModal.vue';
    import DeploymentsStampsDeleteModal from '@/components/modals/DeploymentsStampsDeleteModal.vue';
    import StampDeployModal from '@/components/modals/StampDeployModal.vue';
    import LegendModal from '@/components/modals/LegendModal.vue';
    import ThemeSettings from '@/components/modals/ThemeSettings.vue';
    import * as Sentry from '@sentry/vue';
    import {DeploymentCounterStart, BalanceFilterEnabled, BalanceFilterType} from '@/components/FilterBar/types';
    import {CSSProperties} from 'vue';
    import {Component, Vue, Ref, Watch} from 'vue-facing-decorator';
    import {DEFAULT_SHORTCUTS, DEFAULT_PAGE_TITLE, DEFAULT_VISIBLE_DAYS} from '@/util/Defaults';
    import {DeploymentCounts} from '@/components/types';
    import {BalanceUtil} from '@/util/BalanceUtil';
    import {KeyControlSetting} from '@/components/deploymentAutocomplete/types';
    import {ShortcutType} from '@/types/enums/ShortcutType';
    import TypeUtil from '@/util/TypeUtil';
    import BudgetDayCounterUtil from '@/util/BudgetDayCounterUtil';
    import PlanbordGrid from '@/components/Planbord/PlanbordGrid.vue';
    import {ChangedDeploymentSet} from '@/types/emits';
    import {UserPermissions} from '@/types/enums/UserPermissions';
    import {FeatureToggle as FeatureToggleEnum} from '@/types/enums/FeatureToggle';
    import Dates from '@cquential/date-utils';
    import WorkScheduleUtil from '@/util/WorkScheduleUtil';
    import {StartDateDefaultType} from '@/types/enums/StartDateDefaultType';
    import {ArrayUtils} from '@cquential/utils';
    import RosterRankSetsModal from '@/components/modals/RosterRankSetsModal.vue';
    import {RosterRankSetManager} from '@/util/RosterRankSetManager';
    import DeploymentProposalAcceptModal from '@/components/modals/DeploymentProposalAcceptModal.vue';
    import CompanyEmployeeSelectModal from '@/components/modals/CompanyEmployeeSelectModal.vue';
    import PlanbordCell from '@/components/Planbord/PlanbordCell.vue';
    import GlobalErrorModal from '@/components/modals/GlobalErrorModal.vue';
    import Cryptography from '@/util/Cryptography';
    import Str from '@/util/Str';
    import SentryHandler from '@/util/SentryHandler';
    import AskTenantModal from '@/components/modals/AskTenantModal.vue';
    import RoosterplanningUrlBuilder from '@/util/RoosterplanningUrlBuilder';
    import ThemeOverrideUtil from '@/util/ThemeOverrideUtil';

    @Component({
        components: {
            CompanyEmployeeSelectModal,
            AppFooter,
            AskTenantModal,
            PlanbordRow,
            FilterBar,
            EnvironmentBadge,
            OptionsRow,
            Modals,
            Loader,
            WorkSchedule,
            KeyboardDeploymentAutocomplete,
            ShiftStateUpdateModal,
            StampShiftEditModal,
            ResourceDetailsModal,
            TitleEditModal,
            Tooltip,
            LeavePeriodModal,
            DeploymentCellContextMenu,
            ShortcutModal,
            RosterCommentModal,
            EmploymentCommentModal,
            Wishes,
            DeploymentHistoryModal,
            WarningInformationModal,
            AssignedDaysModal,
            CustomDaysVisibleModal,
            SickPeriodEditModal,
            DeploymentsStampsDeleteModal,
            StampDeployModal,
            LegendModal,
            ThemeSettings,
            RosterRankSetsModal,
            DeploymentProposalAcceptModal,
        },
        emits: [],
    })
    export default class Planbord extends Vue {
        @Ref() public readonly row!: PlanbordRow;

        private static readonly REFRESH_TIMER: number = 60000;
        private static readonly INCLUDES_FOR_STAMP_RELOAD: string[] = [
            'personalStamps.personalStampShifts',
            'personalStamps.personalStampAssignedDays',
            'balances',
        ];

        private static instance: Planbord | null;

        private activeHorizontalPosition: number = -1;
        private activeVerticalPosition: number = -1;
        private activeMarkerIndex: number = 0;
        private optionsRowDataLoaded: boolean = false;
        private planbordIsMounted: boolean = false;
        private showLegend: boolean = false;
        private showThemeSettings: boolean = false;

        private user: RosterUser | null = null;
        private preferences: Record<string, string | number> = {};
        private enabledFeatures: string[] = [];
        private roster: RosterData | undefined = undefined;
        private resourceFunctionNames: Record<number, string> = {};

        private rosters: RosterSelectOption[] = [];
        private companies: Company[] = [];
        private leaveOverviews: LeaveOverview[] = [];
        private leaveKinds: LeaveKind[] = [];
        private workSchedulePositionsPerDay: Record<string, PlanbordWorkSchedulePosition[]> = {};

        private shifts: Record<number, Shift> = {};
        private shiftIdsPerRoster: Record<number, number[]> = {};
        private defaultShiftIdPerRoster: Record<number, number> = {};

        private deploymentCountConfigurations: DeploymentCountConfiguration[] = [];
        private deploymentCountConfigurationBalances: DeploymentCountConfigurationBalances = {};
        private shortcutPreferences: Record<ShortcutType, KeyControlSetting> = {...DEFAULT_SHORTCUTS};

        private viewOptions: ViewOptions = {
            showOnlyNegativeValues: false,
            showFunctions: false,
            showComments: false,
            showStampStartDate: false,
            showCompactMode: false,
            hideTooltip: false,
            useNewWorkloadRequirements: false,
        };

        private rosterViewSettings: RosterViewSettings = {
            rosterId: 0,
            startDate: Dates.Utils.addDays(Dates.Utils.today(), -3),
            visibleDays: 7,
        };

        private showPlusMinusBalanceFrom: Date | false = false;
        private showDeploymentCounterFrom: DeploymentCounterStart = {days: 0, date: null};

        private organisations: Record<string, Organisation> = {};
        private globals: Globals | null = null;
        private projects: Project[] | undefined = undefined;
        private rosterPositionAssignmentGroups: PositionAssignmentSetGroup[] | undefined = undefined;

        private budgetDayCounters: BudgetDayCounters = {};
        private currentRosterName: string = '';

        private lastUpdate: number = Date.now();
        private refreshTimer: number | null = null;

        private readonly emptyPermissionsObject: Readonly<PermissionsForRoster> = Object.freeze({
            [UserPermissions.EditStampPeriod]: false,
            [UserPermissions.ViewConstructionPeriod]: false,
            [UserPermissions.EditConstructionPeriod]: false,
            [UserPermissions.ViewPublishedPeriod]: false,
            [UserPermissions.EditPublishedPeriod]: false,
            [UserPermissions.EditLeavePeriods]: false,
            [UserPermissions.EditSicknessCases]: false,
            [UserPermissions.ChangeStampsToConstruction]: false,
            [UserPermissions.ProposeRoster]: false,
            [UserPermissions.AcceptProposal]: false,
            [UserPermissions.ShowRoster]: false,
            [UserPermissions.WishInputClose]: false,
            [UserPermissions.EditRosterComment]: false,
            [UserPermissions.ReorderUsers]: false,
            [UserPermissions.EditEmploymentComment]: false,
            [UserPermissions.EditWishes]: false,
            [UserPermissions.SetRightsDerived]: false,
            [UserPermissions.EditWorkSchedule]: false,
            [UserPermissions.PutOwnRosterPeopleOnRoster]: false,
            [UserPermissions.ProposeDeploymentsPublishedPeriod]: false,
            [UserPermissions.ProposeDeploymentsConstructionPeriod]: false,
            [UserPermissions.EditRosterRankSets]: false,
        });

        private fakeIdPool: Record<number, true> = {};

        public askTenantId: boolean = false;
        public globalTenant: string = '';
        private accessToken: string | null = import.meta.env.VITE_STORE_ACCESS_TOKEN === 'true' ? sessionStorage.getItem('accessToken') : null;

        public mounted(): void {
            Planbord.instance = this;
            this.initTenant();

            this.planbordIsMounted = true;
            window.addEventListener('keydown', this.keyboardNavigationEventHandler);
            window.addEventListener('click', this.clickHandler);
            this.resetRefreshTimer();
        }

        public beforeUnmount(): void {
            Planbord.instance = null;
            this.planbordIsMounted = false;
            window.removeEventListener('keydown', this.keyboardNavigationEventHandler);
            window.removeEventListener('click', this.clickHandler);
            if (this.refreshTimer !== null) {
                clearTimeout(this.refreshTimer);
            }
        }

        @Watch('globalTenant', {immediate: true})
        public onGlobalTenantChange(tenant: string): void {
            RoosterplanningUrlBuilder.globalTenant = tenant;
        }

        public get shiftsLoaded(): boolean {
            /** @see Loader.isLoaded() */
            // @ts-expect-error TS2339
            return Loader.__vfdConstructor.isLoaded('shifts');
        }

        public get hrCouplingEnabled(): boolean {
            return this.globals?.hrCouplingEnabled ?? false;
        }

        public get oldHrCouplingEnabled(): boolean {
            return this.globals?.oldHrCouplingEnabled ?? false;
        }

        public get enabledBalanceAndCountDisplays(): Record<BalanceFilterType, Record<number, boolean>> {
            return {
                [BalanceFilterType.HourBalance]: this.roster ? BalanceUtil.enabledBalances(this.enabledBalanceFilters, BalanceFilterType.HourBalance) : {},
                [BalanceFilterType.DeploymentCount]: BalanceUtil.enabledBalances(this.enabledBalanceFilters, BalanceFilterType.DeploymentCount),
                [BalanceFilterType.LeaveOverview]: BalanceUtil.enabledBalances(this.enabledBalanceFilters, BalanceFilterType.LeaveOverview),
            };
        }

        public get deploymentCounts(): DeploymentCounts {
            const counts: DeploymentCounts = {resources: {}, companies: {}};
            if (!this.roster || !this.roster.rosterResources || !this.deploymentCountConfigurationBalances) {
                return counts;
            }

            for (const enabledBalanceFilter in this.enabledBalanceFilters) {
                const configId: number = this.enabledBalanceFilters[enabledBalanceFilter].id;
                if (this.enabledBalanceFilters[enabledBalanceFilter].type !== BalanceFilterType.DeploymentCount
                    || !(configId in this.deploymentCountConfigurationBalances)
                ) {
                    continue;
                }

                let resource: PlanbordResource;
                for (resource of this.roster.rosterResources) {
                    if (!resource.id) {
                        continue;
                    }

                    if (!(resource.id in counts['resources'])) {
                        counts['resources'][resource.id] = [];
                    }
                    let count: number = 0;
                    if (resource.id in this.deploymentCountConfigurationBalances[configId]['resources']) {
                        count = this.deploymentCountConfigurationBalances[configId]['resources'][resource.id];
                    }
                    counts['resources'][resource.id].push({
                        configurationId: Number(configId),
                        value: count,
                    });
                }

                for (const company of this.enabledCompanies) {
                    if (!(company.id in counts['companies'])) {
                        counts['companies'][company.id] = [];
                    }
                    let count: number = 0;
                    if (company.id in this.deploymentCountConfigurationBalances[configId]['companies']) {
                        count = this.deploymentCountConfigurationBalances[configId]['companies'][company.id];
                    }
                    counts['companies'][company.id].push({
                        configurationId: Number(configId),
                        value: count,
                    });
                }
            }

            return counts;
        }

        public get shiftIdsOnCurrentRoster(): number[] {
            if (!this.roster) {
                return [];
            }

            return this.shiftIdsPerRoster[this.roster.rosterId] || [];
        }

        public get rosterNames(): Record<number, string> {
            const result: Record<number, string> = {};
            for (const roster of this.rosters) {
                result[Number(roster.value)] = roster.label;
            }

            return result;
        }

        public get organisation(): Organisation | null {
            if (!this.roster) {
                return null;
            }

            return this.organisations[this.roster.organisationId];
        }

        public get enabledCompanies(): CompanyWithDeploymentsAndEmployees[] {
            return this.roster
                ? Object.values(this.roster.companies!).filter((company: Company): boolean => {
                    return !company.endDate || company.endDate >= this.rosterViewSettings.startDate;
                })
                : [];
        }

        public get rosterProposedUntil(): Date | null {
            if (this.roster) {
                return this.roster.proposedUntil || null;
            }

            return null;
        }

        public get enabledBalanceFilters(): Record<string, BalanceFilterEnabled> {
            return 'balanceFilters' in this.preferences ? JSON.parse(String(this.preferences.balanceFilters)) as Record<string, BalanceFilterEnabled> : {};
        }

        public set enabledBalanceFilters(val: Record<string, BalanceFilterEnabled>) {
            this.updatePreference('balanceFilters', JSON.stringify(val));
        }

        public get rosterWidth(): number {
            return this.roster ? this.roster.days.length : 0;
        }

        public get rosterHeight(): number {
            if (!this.roster) {
                return 0;
            }

            const resourceRowCount: number = this.roster.rosterResources.length;
            const companyAndCompanyEmployeeRowCount: number = Object.values(this.roster.companies)
                .map((company: CompanyWithDeploymentsAndEmployees): number => 1 + (Object.values(company.employees).length))
                .reduce((total: number, currentValue: number): number => total + currentValue, 0);

            return resourceRowCount + companyAndCompanyEmployeeRowCount;
        }

        public get allResources(): (ResourceWithKeyedByDateObjects)[] {
            if (this.roster === undefined) {
                return [];
            }

            return [...this.roster.rosterResources, ...this.otherResources];
        }

        public get otherResources(): ResourceWithKeyedByDateObjects[] {
            if (this.roster === undefined) {
                return [];
            }

            return [...this.roster.relatedResources, ...this.roster.resourcesFromOtherRosters, ...this.roster.externalResources];
        }

        public get permissionsPerRoster(): Record<number, PermissionsForRoster> {
            if (this.user === null) {
                return {};
            }

            const result: Record<number, PermissionsForRoster> = {};

            for (const userPermission of this.user!.userPermissions!) {
                const rosterId: number = userPermission.rosterId ?? 0;
                if (!(rosterId in result)) {
                    result[rosterId] = {...this.emptyPermissionsObject};
                }

                result[rosterId][userPermission.permissionId as UserPermissions] = true;
            }

            return result;
        }

        public get permissionsForSelectedRoster(): PermissionsForRoster {
            return this.permissionsPerRoster[this.roster?.rosterId ?? -1] ?? this.emptyPermissionsObject;
        }

        public get viewableRosters(): RosterSelectOption[] {
            if (!this.rosters) {
                return [];
            }

            const hideRostersWithEndDateBeforeDate: Date = new Date();
            hideRostersWithEndDateBeforeDate.setMonth(hideRostersWithEndDateBeforeDate.getMonth() - 3);

            // also defined in class RoosterWeb_Roster_Type
            const allowedRosterTypes: RosterTypeNumber[] = [RosterTypeNumber.Normal, RosterTypeNumber.Picket, RosterTypeNumber.AncillaryActivities];

            return this.rosters.filter((roster: RosterSelectOption): boolean => {
                if (!(roster.data.id in this.permissionsPerRoster)) {
                    return false;
                }

                if (roster.data.endDate && roster.data.endDate < hideRostersWithEndDateBeforeDate) {
                    return false;
                }

                if (!allowedRosterTypes.includes(roster.data.type)) {
                    return false;
                }

                const canViewConstructionPeriod: boolean = this.permissionsPerRoster[roster.data.id][UserPermissions.ViewConstructionPeriod];
                const canViewPublishedPeriod: boolean = this.permissionsPerRoster[roster.data.id][UserPermissions.ViewPublishedPeriod];

                if (canViewConstructionPeriod) {
                    if (canViewPublishedPeriod) {
                        return true;
                    }

                    return roster.data.endDate === null || roster.data.proposedUntil === null || roster.data.endDate > roster.data.proposedUntil;
                } else if (canViewPublishedPeriod) {
                    return roster.data.proposedUntil !== null && (roster.data.beginDate === null || roster.data.beginDate < roster.data.proposedUntil);
                }

                return false;
            });
        }

        public get cell(): PlanbordCell | undefined {
            /** @see PlanbordGrid.getRow() */
            // @ts-expect-error TS2339
            const row: PlanbordGridRow | undefined = PlanbordGrid.__vfdConstructor.getRow(this.activeVerticalPosition);

            return row && row.getCell(this.activeHorizontalPosition);
        }

        public get amountOfMarkers(): number {
            if (this.cell) {
                // It's either one of the other so is always a safe operation.
                return (this.cell.stampShifts?.length ?? 0) + (this.cell.deployments?.length ?? 0);
            }

            return 0;
        }

        public get selectedRosterOption(): RosterSelectOption | undefined {
            return this.rosters.find((roster: RosterSelectOption): boolean => roster.value === this.rosterViewSettings.rosterId);
        }

        public get selectableDateRange(): NullableRange<Date> {
            if (!this.selectedRosterOption) {
                return {min: null, max: null};
            }

            if (!(this.selectedRosterOption.value in this.permissionsPerRoster)) {
                console.error('Roster without permissions selected.');

                return {min: null, max: null};
            }

            const canViewPublished: boolean = this.permissionsPerRoster[this.selectedRosterOption.value][UserPermissions.ViewPublishedPeriod];
            const canViewConstruction: boolean = this.permissionsPerRoster[this.selectedRosterOption.value][UserPermissions.ViewConstructionPeriod];

            if (!canViewPublished && !canViewConstruction) {
                console.error('Unviewable roster selected.');

                return {min: null, max: null};
            }

            let earliestSelectableDate: Date | null;
            if (!canViewPublished && this.selectedRosterOption.data.proposedUntil) {
                earliestSelectableDate = Dates.Utils.dateToAmsterdamMidnightDate(Dates.Utils.addDays(this.selectedRosterOption.data.proposedUntil, 1));
            } else {
                earliestSelectableDate = this.selectedRosterOption.data.beginDate;
            }

            const latestViewableDate: Date | null = canViewConstruction ? this.selectedRosterOption.data.endDate : this.selectedRosterOption.data.proposedUntil;
            const latestSelectableDate: Date | null = latestViewableDate ? Dates.Utils.dateToAmsterdamMidnightDate(Dates.Utils.addDays(latestViewableDate, -this.rosterViewSettings.visibleDays + 1)) : null;

            return {min: earliestSelectableDate, max: latestSelectableDate};
        }

        public get isActiveSelectedCell(): boolean {
            /** @see PlanbordGrid.getSelectedCells() */
            // @ts-expect-error TS2339
            return PlanbordGrid.__vfdConstructor.getSelectedCells().some((cell: CellCoordinates): boolean => cell.x === this.activeHorizontalPosition && cell.y === this.activeVerticalPosition);
        }

        public get canEditWorkSchedules(): boolean {
            return this.permissionsForSelectedRoster[UserPermissions.EditWorkSchedule] && this.areWorkSchedulesEnabled;
        }

        public get areWorkSchedulesEnabled(): boolean {
            return this.enabledFeatures.includes(FeatureToggleEnum.EnableWorkSchedule);
        }

        public get allowRosterActionsToAnyWeekDay(): boolean {
            return this.enabledFeatures.includes(FeatureToggleEnum.AllowRosterActionsForAnyWeekDay);
        }

        public get themeOverrides(): CSSProperties {
            return new ThemeOverrideUtil(this.preferences).themeOverrides;
        }

        @Watch('themeOverrides', {immediate: true})
        public changeThemeOverrides(): void {
            let name: keyof CSSProperties;
            for (name in this.themeOverrides) {
                const value: string | number | undefined = this.themeOverrides[name];
                if (value === undefined) {
                    document.body.style.removeProperty(name);
                } else {
                    document.body.style.setProperty(name, String(value));
                }
            }
        }

        public get informationColumns(): number {
            return 1 + Number(this.viewOptions.showFunctions) + Number(this.viewOptions.showComments) + Number(this.viewOptions.showStampStartDate);
        }

        public get dayColumns(): number {
            return this.roster?.days.length ?? 0;
        }

        public get gridDefinitionData(): CSSProperties {
            const result: CSSProperties = {};

            result['--show-function-cell'] = this.viewOptions.showFunctions ? 'block' : 'none';
            result['--show-comment-cell'] = this.viewOptions.showComments ? 'block' : 'none';
            result['--show-stamp-start-date-cell'] = this.viewOptions.showStampStartDate ? 'block' : 'none';

            return result;
        }

        public get themeName(): string | undefined {
            return 'planbordTheme' in this.preferences ? String(this.preferences.planbordTheme) : undefined;
        }

        @Watch('themeName', {immediate: true})
        public changeThemeName(): void {
            if (this.themeName === undefined) {
                delete document.body.dataset.theme;
            } else {
                document.body.dataset.theme = this.themeName;
            }
        }

        public get uiComponentsThemeName(): 'light' | 'dark' | undefined {
            if (this.themeName === undefined) {
                return undefined;
            }

            return this.themeName === 'dark' ? 'dark' : 'light';
        }

        @Watch('uiComponentsThemeName', {immediate: true})
        public changeUiComponentsThemeName(): void {
            if (this.uiComponentsThemeName === undefined) {
                delete document.body.dataset.cqUiTheme;
            } else {
                document.body.dataset.cqUiTheme = this.uiComponentsThemeName;
            }
        }

        public applyTenant(): void {
            this.askTenantId = false;
            this.initTenant();
        }

        public initTenant(): void {
            const apiUrl: string | null = RoosterplanningUrlBuilder.getApiUrl();
            if (apiUrl === null) {
                this.askTenantId = true;

                return;
            }

            this.initApi(apiUrl);
        }

        private initApi(apiUrl: string): void {
            ApiClient.setURL(apiUrl);
            ApiClient.setIncludeDeploymentsWithOverlappingSpecifications(true);

            if (this.accessToken) {
                ApiClient.setToken(this.accessToken);
                SentryHandler.initialize(this.$.appContext.app);
                this.initializePlanbord();
            } else {
                this.initOAuthFlow().catch((): void => {
                    /** @see GlobalErrorModal.globalErrorHandler() */
                    // @ts-expect-error TS2339
                    GlobalErrorModal.__vfdConstructor.open('Planbord kon niet opgestart worden. Probeer het later opnieuw.', true);
                });
            }
        }

        private async initOAuthFlow(): Promise<void> {
            const urlParams: URLSearchParams = new URLSearchParams(document.location.search);
            if (urlParams.has('code') && urlParams.has('state')) {
                const verifier: string | null = sessionStorage.getItem('verifier');
                const state: PlanbordQueryParameters = PlanbordQueryFilter.getFromState(urlParams);

                if (verifier === null) {
                    throw new Error('Cannot start planbord; no verifier was found when returning from IdP.');
                }

                ApiClient.instance.fetchAccessToken(Number(import.meta.env.VITE_OAUTH_CLIENT_ID), document.location.origin, verifier, urlParams.get('code')!)
                    .then((token: OAuthToken): void => {
                        const url: URL = new URL(location.origin);
                        PlanbordQueryFilter.setFiltered(url.searchParams, state);
                        history.replaceState(null, '', url);

                        if (import.meta.env.VITE_STORE_ACCESS_TOKEN === 'true') {
                            sessionStorage.setItem('accessToken', token.accessToken);
                        }

                        ApiClient.setToken(token.accessToken);
                        SentryHandler.initialize(this.$.appContext.app);

                        this.initializePlanbord();
                    })
                    .catch((error: ApiError): void => {
                        /** @see GlobalErrorModal.globalErrorHandler() */
                        // @ts-expect-error TS2339
                        GlobalErrorModal.__vfdConstructor.globalErrorHandler(error);
                    });
            } else {
                const tenant: string = RoosterplanningUrlBuilder.getTenant()!;
                const verifier: string = Str.randomString(64);
                sessionStorage.setItem('verifier', verifier);

                const state: PlanbordQueryParameters = PlanbordQueryFilter.getFiltered(urlParams);
                state.tenant = tenant;

                const getParams: Record<string, string> = {
                    client_id: import.meta.env.VITE_OAUTH_CLIENT_ID,
                    redirect_uri: location.origin,
                    response_type: 'code',
                    state: JSON.stringify(state),
                    code_challenge: await Cryptography.generateOauthChallenge(verifier),
                    code_challenge_method: 'S256',
                };
                location.href = `${RoosterplanningUrlBuilder.getApiUrl()}oauth/authorize?${Str.queryString(getParams)}`;
            }
        }

        public setActiveCell(
            horizontalPosition: number,
            verticalPosition: number,
            openDropdown: boolean,
            forceAdd: boolean = false,
            index: number = 0,
            isProposal: boolean = false,
        ): void {
            if (!this.planbordIsMounted) {
                return;
            }

            if (openDropdown) {
                /** @see PlanbordGrid.resetGrid() */
                // @ts-expect-error TS2339
                PlanbordGrid.__vfdConstructor.resetGrid();
            }
            this.activeHorizontalPosition = horizontalPosition;
            this.activeVerticalPosition = verticalPosition;
            this.activateCell(index, openDropdown, forceAdd, isProposal);
        }

        public scrollActiveCellIntoView(dropdownIsBeingOpened: boolean): void {
            const activeCell: Element | undefined = this.cell ? this.cell.$el : undefined;
            if (!activeCell) {
                console.error('No active cell to scroll into view...');

                return;
            }

            CellIntoViewScroller.scrollCellIntoView(activeCell, dropdownIsBeingOpened);
        }

        private updatePlanbord(): void {
            this.lastUpdate = Date.now();
            this.processDeploymentChanges();
            this.processOpenShiftChanges();
            this.resetRefreshTimer();
        }

        private resetRefreshTimer(): void {
            if (this.refreshTimer !== null) {
                clearTimeout(this.refreshTimer);
            }
            this.refreshTimer = setTimeout((): void => {
                if (!this.roster) {
                    return;
                }
                this.updatePlanbord();
            }, Planbord.REFRESH_TIMER);
        }

        private processDeploymentChanges(): void {
            const rosterId: number = this.roster!.rosterId;
            ApiClient.instance
                .fetchDeploymentsChangesSince(
                    rosterId,
                    new Date(this.lastUpdate - Planbord.REFRESH_TIMER),
                    this.rosterViewSettings.startDate,
                    Dates.Utils.addDays(this.rosterViewSettings.startDate, this.rosterViewSettings.visibleDays - 1),
                    ['newDeployment', 'newAssignedDay'],
                )
                .then((deploymentHistoryEntries: DeploymentHistory[]): void => {
                    if (!this.roster || rosterId !== this.roster.rosterId) {
                        return;
                    }
                    const resourcesToRefresh: number[] = [];
                    for (const deploymentHistoryEntry of deploymentHistoryEntries) {
                        const deploymentDate: string = Dates.Formatter.formatDateISO(new Date(deploymentHistoryEntry.deplDate));
                        const resource: ResourceWithKeyedByDateObjects | undefined = this.allResources.find((resource: ResourceWithKeyedByDateObjects): boolean => resource.id === deploymentHistoryEntry.resourceId);

                        if (resource === undefined) {
                            continue;
                        }

                        if (deploymentHistoryEntry.action === 'remove' || deploymentHistoryEntry.action === 'edit') {
                            // remove old deployment & assigned day
                            if (deploymentHistoryEntry.oldDeplId && resource.deployments[deploymentDate]) {
                                const oldDeploymentIndex: number = resource.deployments[deploymentDate].findIndex((value: Deployment): boolean => value.id === deploymentHistoryEntry.oldDeplId);
                                if (oldDeploymentIndex >= 0) {
                                    resource.deployments[deploymentDate].splice(oldDeploymentIndex, 1);
                                }
                            }

                            if (deploymentHistoryEntry.oldAssignedDayId && resource.assignedDays[deploymentDate]) {
                                const oldAssignedDayIndex: number = resource.assignedDays[deploymentDate].findIndex((value: AssignedDay): boolean => value.id === deploymentHistoryEntry.oldAssignedDayId);
                                if (oldAssignedDayIndex >= 0) {
                                    resource.assignedDays[deploymentDate].splice(oldAssignedDayIndex, 1);
                                }
                            }
                        }

                        if (deploymentHistoryEntry.action === 'insert' || deploymentHistoryEntry.action === 'edit') {
                            if (deploymentHistoryEntry.newDeplId) {
                                // add new deployment
                                resource.deployments[deploymentDate] ??= [];
                                const newDeploymentIndex: number = resource.deployments[deploymentDate].findIndex((value: Deployment): boolean => value.id === deploymentHistoryEntry.newDeplId);
                                if (newDeploymentIndex === -1) {
                                    resource.deployments[deploymentDate].push(deploymentHistoryEntry.newDeployment!);
                                }
                            }

                            if (deploymentHistoryEntry.newAssignedDayId) {
                                // add new assigned day
                                resource.assignedDays[deploymentDate] ??= [];
                                const newAssignedDayIndex: number = resource.assignedDays[deploymentDate].findIndex((value: AssignedDay): boolean => value.id === deploymentHistoryEntry.newAssignedDayId);
                                if (newAssignedDayIndex === -1) {
                                    resource.assignedDays[deploymentDate].push(deploymentHistoryEntry.newAssignedDay!);
                                }
                            }
                        }

                        resourcesToRefresh.push(resource.id!);
                    }

                    if (resourcesToRefresh.length > 0) {
                        this.updateResources(resourcesToRefresh, [
                            'balances',
                            'atwIssues',
                        ]);
                        this.loadDeploymentCountConfigurationBalances(resourcesToRefresh);
                    }
                })
                /** @see GlobalErrorModal.globalErrorHandler() */
                // @ts-expect-error TS2339
                .catch(GlobalErrorModal.__vfdConstructor.globalErrorHandler);
        }

        private processOpenShiftChanges(): void {
            const rosterId: number = this.roster!.rosterId;
            ApiClient.instance.fetchOpenShiftsChangedSince(
                rosterId,
                new Date(this.lastUpdate - Planbord.REFRESH_TIMER),
                this.rosterViewSettings.startDate,
                Dates.Utils.addDays(this.rosterViewSettings.startDate, this.rosterViewSettings.visibleDays - 1),
                [
                    'applications',
                    'specifications',
                    'publishedToOtherCompanies',
                ])
                .then((openShifts: OpenShift[]): void => {
                    if (!this.roster || this.roster.rosterId !== rosterId) {
                        return;
                    }

                    for (const openShift of openShifts) {
                        const date: string = Dates.Formatter.formatDateComputerFriendly(openShift.shiftDate);

                        if (!(openShift.shiftId in this.roster.openShifts)) {
                            this.roster.openShifts[openShift.shiftId] = {};
                        }

                        if (!(date in this.roster.openShifts[openShift.shiftId])) {
                            this.roster.openShifts[openShift.shiftId][date] = [];
                        }

                        const index: number = this.roster.openShifts[openShift.shiftId][date].findIndex((currentOpenShift: OpenShift): boolean => {
                            return currentOpenShift.id === openShift.id;
                        });

                        if (![OpenShiftState.NEW, OpenShiftState.APPLIED].includes(openShift.state)) {
                            if (index !== -1) {
                                this.roster.openShifts[openShift.shiftId][date].splice(index, 1);
                            }
                        } else if (index === -1) {
                            this.roster.openShifts[openShift.shiftId][date].push(openShift);
                        } else {
                            this.roster.openShifts[openShift.shiftId][date][index] = openShift;
                        }
                    }
                })
                /** @see GlobalErrorModal.globalErrorHandler() */
                // @ts-expect-error TS2339
                .catch(GlobalErrorModal.__vfdConstructor.globalErrorHandler);
        }

        // Method for initialising the planbord component
        private initializePlanbord(): void {
            // Show loader during initialisation
            /** @see Loader.setPending() */
            // @ts-expect-error TS2339
            Loader.__vfdConstructor.setPending('rosters');
            /** @see Loader.setPending() */
            // @ts-expect-error TS2339
            Loader.__vfdConstructor.setPending('shifts');
            /** @see Loader.setPending() */
            // @ts-expect-error TS2339
            Loader.__vfdConstructor.setPending('configuration');

            const userRosterListPromises: Promise<void>[] = [];
            userRosterListPromises.push(this.loadUser());
            userRosterListPromises.push(this.loadRosterList());
            Promise.all(userRosterListPromises)
                .then((): void => {
                    this.initializeRosterViewSettings();
                    this.optionsRowDataLoaded = true;
                })
                .catch(this.loadingFailed);

            const promises: Promise<void>[] = [];
            promises.push(this.loadShifts());
            promises.push(this.loadResourceFunctions());
            promises.push(this.loadFeatureToggles());
            promises.push(this.loadOrganisations());
            promises.push(this.loadCompanies());
            promises.push(this.loadLeaveOverviews());
            promises.push(this.loadLeaveKinds());
            promises.push(this.loadGlobals());
            promises.push(this.loadDeploymentCountConfigurations());
            promises.push(this.loadProjects());
            Promise.all(promises)
                .then((): void => {
                    if (!this.enabledFeatures.includes('1850_ShowProjectAndActivitiesInPlanbord')) {
                        this.projects = undefined;
                    }
                    /** @see Loader.isProcessing() */
                    // @ts-expect-error TS2339
                    if (!Loader.__vfdConstructor.isProcessing('roster') && this.rosterViewSettings.rosterId > 0) {
                        this.reloadPlanbord();
                    }
                })
                .catch(this.loadingFailed);
        }

        private loadingFailed(e: Error): void {
            console.error(e);
            /** @see Notify.createNotification() */
            // @ts-expect-error TS2339
            Notify.__vfdConstructor.createNotification({text: 'Er ging iets mis.', type: 'error'});
        }

        private getDefaultStartDate(rosterData: RosterOptionData | null): Date {
            const startDateDefault: StartDateDefaultType = 'startDateDefaultType' in this.preferences ? this.preferences.startDateDefaultType as StartDateDefaultType : StartDateDefaultType.ThreeDaysBeforeToday;

            switch (startDateDefault) {
                case StartDateDefaultType.Today:
                    return Dates.Utils.today();
                case StartDateDefaultType.ThisWeekMonday:
                    return Dates.Utils.startOfWeek();
                case StartDateDefaultType.FirstDayNotPublished:
                    if (rosterData) {
                        if (rosterData.publishedUntil !== null) {
                            return Dates.Utils.addDays(rosterData.publishedUntil, 1);
                        } else if (rosterData.beginDate !== null) {
                            return rosterData.beginDate!;
                        }
                    }
                // eslint-disable-next-line no-fallthrough
                case StartDateDefaultType.ThreeDaysBeforeToday:
                    return Dates.Utils.addDays(Dates.Utils.today(), -3);
            }
        }

        private initializeRosterViewSettings(): void {
            // cache to prevent additional loops
            let basicRosterData: RosterOptionData | null = null;

            // load defaults
            let visibleDays: number = 'daysVisible' in this.preferences ? Number(this.preferences.daysVisible) : DEFAULT_VISIBLE_DAYS;
            let rosterId: number = 0;

            // check if rosterId was explicitly requested
            const searchParams: URLSearchParams = new URLSearchParams(document.location.search);
            const requestedRosterId: string | null = searchParams.get('rosterId');
            if (requestedRosterId !== null) {
                const requestedRosterIdNumber: number = Number(requestedRosterId);
                if (!Number.isNaN(requestedRosterIdNumber) && rosterId !== requestedRosterIdNumber) {
                    for (const roster of this.viewableRosters) {
                        if (roster.data.id === requestedRosterIdNumber) {
                            rosterId = requestedRosterIdNumber;
                            break;
                        }
                    }
                }
            }

            // set rosterId if user has access to only 1 roster
            if (rosterId === 0 && this.viewableRosters.length === 1) {
                rosterId = this.viewableRosters[0].data.id;
            }

            if (rosterId > 0) {
                for (const roster of this.viewableRosters) {
                    if (roster.data.id === rosterId) {
                        // copy date from roster to
                        basicRosterData = roster.data;
                        break;
                    }
                }
            }

            let startDate: Date = this.getDefaultStartDate(basicRosterData);

            const requestedStartDate: string | null = searchParams.get('startDate');
            if (requestedStartDate !== null) {
                const requestedStartDateObject: Date = new Date(requestedStartDate);
                if (requestedStartDateObject.valueOf() !== startDate.valueOf() && (!basicRosterData || basicRosterData.beginDate === null || requestedStartDateObject >= basicRosterData.beginDate)) {
                    startDate = requestedStartDateObject;
                }
            }

            const requestedVisibleDays: string | null = searchParams.get('shownDays');
            if (requestedVisibleDays !== null) {
                const requestedVisibleDaysNumber: number = Number(requestedVisibleDays);
                if (requestedVisibleDaysNumber >= CustomDaysVisibleModal.MINIMUM_VISIBLE_DAYS && requestedVisibleDaysNumber <= CustomDaysVisibleModal.MAXIMUM_VISIBLE_DAYS) {
                    visibleDays = requestedVisibleDaysNumber;
                }
            }

            Object.assign(this.rosterViewSettings, {
                rosterId,
                startDate,
                visibleDays,
            });
            // TODO, why is this using Object.assign? Doesn't seem to be needed for reactivity (Object.assign isn't even mentioned in the documentation for reactivity: https://vuejs.org/api/reactivity-core.html)
            // it seems an overly complex way to write 3 properties (the simple way being just writing the properties [e.g. rosterViewSettings.rosterId = rosterId;])

            this.updateViewSettingsForPermissions();

            this.viewOptions.showOnlyNegativeValues = 'showOnlyNegativeValues' in this.preferences ? !!this.preferences.showOnlyNegativeValues : false;
            this.viewOptions.showFunctions = 'showFunctions' in this.preferences ? !!this.preferences.showFunctions : false;
            this.viewOptions.showComments = 'showComments' in this.preferences ? !!this.preferences.showComments : false;
            this.viewOptions.showStampStartDate = 'showStampStartDate' in this.preferences ? !!this.preferences.showStampStartDate : false;
            this.viewOptions.showCompactMode = 'showCompactMode' in this.preferences ? !!this.preferences.showCompactMode : false;
            this.viewOptions.hideTooltip = 'hideTooltip' in this.preferences ? !!this.preferences.hideTooltip : false;
        }

        private updateViewSettingsForPermissions(): void {
            if (this.selectableDateRange.min && this.rosterViewSettings.startDate < this.selectableDateRange.min) {
                this.rosterViewSettings.startDate = new Date(this.selectableDateRange.min);
            }

            if (this.selectableDateRange.max && this.rosterViewSettings.startDate > this.selectableDateRange.max) {
                this.rosterViewSettings.startDate = new Date(this.selectableDateRange.max);
            }
        }

        private updateViewSettings(newValue: RosterViewSettings): void {
            if (this.rosterViewSettings.rosterId !== newValue.rosterId) {
                this.setSearchParameter('rosterId', newValue.rosterId);
            }

            const startDateHasChanged: boolean = this.rosterViewSettings.startDate !== newValue.startDate;

            if (this.rosterViewSettings.visibleDays !== newValue.visibleDays) {
                this.setSearchParameter('shownDays', newValue.visibleDays);
            }

            Object.assign(this.rosterViewSettings, newValue);
            this.updatePreference('daysVisible', this.rosterViewSettings.visibleDays);

            this.updateViewSettingsForPermissions();

            if (startDateHasChanged) {
                this.setSearchParameter('startDate', Dates.Formatter.formatDateComputerFriendly(this.rosterViewSettings.startDate));
            }

            this.reloadPlanbord();
        }

        private setSearchParameter(paramName: string, value: string | number): void {
            const url: URL = new URL(window.location.href);
            url.searchParams.set(paramName, String(value));

            history.pushState({}, '', url);
        }

        private loadRosterList(): Promise<void> {
            /** @see Loader.setLoading() */
            // @ts-expect-error TS2339
            Loader.__vfdConstructor.setLoading('rosters');

            return ApiClient.instance.fetchRosters(['allowedFunctions'])
                .then((loadedRosters: Roster[]): void => {
                    Object.assign(this.rosters, loadedRosters.map((roster: Roster): RosterSelectOption => {
                        return {
                            label: roster.name,
                            value: roster.id!,
                            data: {
                                beginDate: roster.beginDate,
                                endDate: roster.endDate,
                                id: roster.id!,
                                name: roster.name,
                                proposedUntil: roster.proposedUntil,
                                publishedUntil: roster.publishedUntil,
                                organisationId: roster.organisationId,
                                allowedFunctions: roster.allowedFunctions,
                                type: roster.type,
                                unpublishedPartVisibleUntil: roster.unpublishedPartVisibleUntil,
                                wishInputClosedUntil: roster.wishInputClosedUntil,
                                wishInputEnabled: roster.wishInputEnabled,
                            },
                        };
                    }));
                    /** @see Loader.setLoaded() */
                    // @ts-expect-error TS2339
                    Loader.__vfdConstructor.setLoaded('rosters');
                })
                .catch((e: ApiError): void => {
                    /** @see Loader.setError() */
                    // @ts-expect-error TS2339
                    Loader.__vfdConstructor.setError('rosters');
                    /** @see GlobalErrorModal.globalErrorHandler() */
                    // @ts-expect-error TS2339
                    GlobalErrorModal.__vfdConstructor.globalErrorHandler(e);
                });
        }

        private loadUser(): Promise<void> {
            /** @see Loader.setLoading() */
            // @ts-expect-error TS2339
            Loader.__vfdConstructor.setLoading('configuration');

            return ApiClient.instance.fetchCurrentUser(['preferences', 'userPermissions'])
                .then((rosterUser: RosterUser): void => {
                    this.user = rosterUser;
                    Object.assign(this.preferences, {});

                    for (const preference of rosterUser.preferences!) {
                        let name: string = preference.name;
                        if (name.startsWith(ApiClient.PREFERENCE_NAME_PREFIX)) {
                            name = name.substr(ApiClient.PREFERENCE_NAME_PREFIX.length);
                        }

                        this.preferences[name] = preference.value;
                    }

                    this.combineDefaultWithPreferenceShortcuts();
                    Sentry.getCurrentScope().setUser({
                        username: rosterUser.username,
                        ip_address: '{{auto}}',
                    });

                    this.showPlusMinusBalanceFrom = 'plusMinusHoursShownFrom' in this.preferences ? new Date(this.preferences.plusMinusHoursShownFrom) : false;

                    let deploymentCountFrom: DeploymentCounterStart = {days: 0, date: null};
                    if ('deploymentCounterFrom' in this.preferences) {
                        if (typeof this.preferences.deploymentCounterFrom === 'string') {
                            deploymentCountFrom = {date: new Date(this.preferences.deploymentCounterFrom), days: null};
                        } else {
                            deploymentCountFrom = {days: this.preferences.deploymentCounterFrom, date: null};
                        }
                    }
                    this.updateDeploymentCounterFrom(deploymentCountFrom);
                    /** @see Loader.setLoaded() */
                    // @ts-expect-error TS2339
                    Loader.__vfdConstructor.setLoaded('configuration');
                }).catch((e: ApiError): void => {
                    /** @see Loader.setError() */
                    // @ts-expect-error TS2339
                    Loader.__vfdConstructor.setError('configuration');
                    /** @see GlobalErrorModal.globalErrorHandler() */
                    // @ts-expect-error TS2339
                    GlobalErrorModal.__vfdConstructor.globalErrorHandler(e);
                });
        }

        private combineDefaultWithPreferenceShortcuts(): void {
            const namePrefix: string = 'shortcut';
            for (const preference in this.preferences) {
                if (preference.startsWith(namePrefix)) {
                    const preferenceName: string = preference.substr(namePrefix.length) as ShortcutType;
                    if (!(preferenceName in ShortcutType)) {
                        console.error('Invalid shortcut found: ' + preferenceName);
                        continue;
                    }

                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    const object: any = JSON.parse(String(this.preferences[preference]));
                    if (!TypeUtil.isKeyControlSetting(object)) {
                        console.error('Invalid shortcut value found:', object);
                        continue;
                    }

                    this.shortcutPreferences[preferenceName as ShortcutType] = object;
                }
            }
        }

        private loadResourceFunctions(): Promise<void> {
            /** @see Loader.setLoading() */
            // @ts-expect-error TS2339
            Loader.__vfdConstructor.setLoading('configuration');

            return ApiClient.instance.fetchResourceFunctions()
                .then((functions: ResourceFunction[]): void => {
                    for (const resourceFunction of functions) {
                        this.resourceFunctionNames[resourceFunction.id!] = resourceFunction.name;
                    }
                    /** @see Loader.setLoaded() */
                    // @ts-expect-error TS2339
                    Loader.__vfdConstructor.setLoaded('configuration');
                }).catch((e: ApiError): void => {
                    /** @see Loader.setError() */
                    // @ts-expect-error TS2339
                    Loader.__vfdConstructor.setError('configuration');
                    /** @see GlobalErrorModal.globalErrorHandler() */
                    // @ts-expect-error TS2339
                    GlobalErrorModal.__vfdConstructor.globalErrorHandler(e);
                });
        }

        private loadFeatureToggles(): Promise<void> {
            /** @see Loader.setLoading() */
            // @ts-expect-error TS2339
            Loader.__vfdConstructor.setLoading('configuration');

            return ApiClient.instance.fetchFeatureToggles()
                .then((featureToggles: FeatureToggle[]): void => {
                    this.enabledFeatures = featureToggles
                        .filter((toggle: FeatureToggle): boolean => toggle.activated)
                        .map((toggle: FeatureToggle): string => toggle.name);

                    /** @see Loader.setLoaded() */
                    // @ts-expect-error TS2339
                    Loader.__vfdConstructor.setLoaded('configuration');
                })
                .catch((e: ApiError): void => {
                    /** @see Loader.setError() */
                    // @ts-expect-error TS2339
                    Loader.__vfdConstructor.setError('configuration');
                    /** @see GlobalErrorModal.globalErrorHandler() */
                    // @ts-expect-error TS2339
                    GlobalErrorModal.__vfdConstructor.globalErrorHandler(e);
                });
        }

        private loadShifts(): Promise<void> {
            /** @see Loader.setLoading() */
            // @ts-expect-error TS2339
            Loader.__vfdConstructor.setLoading('shifts');

            return ApiClient.instance.fetchShiftsWithTemplates([], ['specifications'])
                .then((loadedShifts: Shift[]): void => {
                    for (const shift of loadedShifts) {
                        this.shiftIdsPerRoster[shift.rosterId] ??= [];
                        this.shiftIdsPerRoster[shift.rosterId].push(shift.id);
                        this.shifts[shift.id] = shift;

                        if (shift.template!.isDefault) {
                            this.defaultShiftIdPerRoster[shift.rosterId] = shift.id;
                        }
                    }
                    /** @see Loader.setLoaded() */
                    // @ts-expect-error TS2339
                    Loader.__vfdConstructor.setLoaded('shifts');
                })
                .catch((e: ApiError): void => {
                    /** @see Loader.setError() */
                    // @ts-expect-error TS2339
                    Loader.__vfdConstructor.setError('shifts');
                    /** @see GlobalErrorModal.globalErrorHandler() */
                    // @ts-expect-error TS2339
                    GlobalErrorModal.__vfdConstructor.globalErrorHandler(e);
                });
        }

        private loadCompanies(): Promise<void> {
            /** @see Loader.setLoading() */
            // @ts-expect-error TS2339
            Loader.__vfdConstructor.setLoading('configuration');

            return ApiClient.instance.fetchCompanies()
                .then((loadedCompanies: Company[]): void => {
                    Object.assign(this.companies, loadedCompanies);
                    /** @see Loader.setLoaded() */
                    // @ts-expect-error TS2339
                    Loader.__vfdConstructor.setLoaded('configuration');
                })
                .catch((e: ApiError): void => {
                    /** @see Loader.setError() */
                    // @ts-expect-error TS2339
                    Loader.__vfdConstructor.setError('configuration');
                    /** @see GlobalErrorModal.globalErrorHandler() */
                    // @ts-expect-error TS2339
                    GlobalErrorModal.__vfdConstructor.globalErrorHandler(e);
                });
        }

        private loadGlobals(): Promise<void> {
            /** @see Loader.setLoading() */
            // @ts-expect-error TS2339
            Loader.__vfdConstructor.setLoading('configuration');

            return ApiClient.instance.fetchGlobals()
                .then((loadedGlobals: Globals): void => {
                    this.globals = loadedGlobals;
                    /** @see Loader.setLoaded() */
                    // @ts-expect-error TS2339
                    Loader.__vfdConstructor.setLoaded('configuration');
                })
                .catch((e: ApiError): void => {
                    /** @see Loader.setError() */
                    // @ts-expect-error TS2339
                    Loader.__vfdConstructor.setError('configuration');
                    /** @see GlobalErrorModal.globalErrorHandler() */
                    // @ts-expect-error TS2339
                    GlobalErrorModal.__vfdConstructor.globalErrorHandler(e);
                });
        }

        private loadLeaveOverviews(): Promise<void> {
            /** @see Loader.setLoading() */
            // @ts-expect-error TS2339
            Loader.__vfdConstructor.setLoading('configuration');

            return ApiClient.instance.fetchLeaveOverviewsForPlanbord()
                .then((loadedLeaveOverviews: LeaveOverview[]): void => {
                    Object.assign(this.leaveOverviews, loadedLeaveOverviews);
                    /** @see Loader.setLoaded() */
                    // @ts-expect-error TS2339
                    Loader.__vfdConstructor.setLoaded('configuration');
                })
                .catch((e: ApiError): void => {
                    /** @see Loader.setError() */
                    // @ts-expect-error TS2339
                    Loader.__vfdConstructor.setError('configuration');
                    /** @see GlobalErrorModal.globalErrorHandler() */
                    // @ts-expect-error TS2339
                    GlobalErrorModal.__vfdConstructor.globalErrorHandler(e);
                });
        }

        private loadLeaveKinds(): Promise<void> {
            /** @see Loader.setLoading() */
            // @ts-expect-error TS2339
            Loader.__vfdConstructor.setLoading('configuration');

            return ApiClient.instance.fetchLeaveKinds().then((fetchedLeaveKinds: LeaveKind[]): void => {
                this.leaveKinds = fetchedLeaveKinds;
                /** @see Loader.setLoaded() */
                // @ts-expect-error TS2339
                Loader.__vfdConstructor.setLoaded('configuration');
            })
                .catch((e: ApiError): void => {
                    /** @see Loader.setError() */
                    // @ts-expect-error TS2339
                    Loader.__vfdConstructor.setError('configuration');
                    /** @see GlobalErrorModal.globalErrorHandler() */
                    // @ts-expect-error TS2339
                    GlobalErrorModal.__vfdConstructor.globalErrorHandler(e);
                });
        }

        private loadProjects(): Promise<void> {
            /** @see Loader.setLoading() */
            // @ts-expect-error TS2339
            Loader.__vfdConstructor.setLoading('configuration');

            return ApiClient.instance.fetchProjects(['activities'])
                .then((loadedProjects: Project[]): void => {
                    this.projects = loadedProjects.sort((a: Project, b: Project): number => {
                        if (!a.grouping) {
                            if (!b.grouping) {
                                return 0;
                            }

                            return 1;
                        }
                        if (!b.grouping) {
                            return -1;
                        }

                        return a.grouping.localeCompare(b.grouping);
                    });
                    /** @see Loader.setLoaded() */
                    // @ts-expect-error TS2339
                    Loader.__vfdConstructor.setLoaded('configuration');
                })
                .catch((e: ApiError): void => {
                    /** @see Loader.setError() */
                    // @ts-expect-error TS2339
                    Loader.__vfdConstructor.setError('configuration');
                    /** @see GlobalErrorModal.globalErrorHandler() */
                    // @ts-expect-error TS2339
                    GlobalErrorModal.__vfdConstructor.globalErrorHandler(e);
                });
        }

        private loadDeploymentCountConfigurations(): Promise<void> {
            /** @see Loader.setLoading() */
            // @ts-expect-error TS2339
            Loader.__vfdConstructor.setLoading('configuration');

            return ApiClient.instance.fetchDeploymentCountConfigurations([], true)
                .then((configurations: DeploymentCountConfiguration[]): void => {
                    Object.assign(this.deploymentCountConfigurations, configurations);
                    /** @see Loader.setLoaded() */
                    // @ts-expect-error TS2339
                    Loader.__vfdConstructor.setLoaded('configuration');
                })
                .catch((e: ApiError): void => {
                    /** @see Loader.setError() */
                    // @ts-expect-error TS2339
                    Loader.__vfdConstructor.setError('configuration');
                    /** @see GlobalErrorModal.globalErrorHandler() */
                    // @ts-expect-error TS2339
                    GlobalErrorModal.__vfdConstructor.globalErrorHandler(e);
                });
        }

        private loadDeploymentCountConfigurationBalances(resourceIds: number[] = []): Promise<void> {
            const fromDate: Date = this.showDeploymentCounterFrom.date
                ?? Dates.Utils.addDays(this.rosterViewSettings.startDate, -this.showDeploymentCounterFrom.days);
            const toDate: Date = Dates.Utils.addDays(this.rosterViewSettings.startDate, this.rosterViewSettings.visibleDays - 1);

            if (resourceIds.length === 0) {
                resourceIds = this.allResources.map((resource: ResourceWithKeyedByDateObjects): number => resource.id ?? -1) ?? [];
            }

            /** @see Loader.setLoading() */
            // @ts-expect-error TS2339
            Loader.__vfdConstructor.setLoading('hourCounter');

            return ApiClient.instance.fetchDeploymentCountConfigurationBalances(fromDate, toDate, ArrayUtils.unique(resourceIds), true)
                .then((balances: DeploymentCountConfigurationBalances): void => {
                    if (resourceIds.length === 0) {
                        Object.assign(this.deploymentCountConfigurationBalances, balances);
                    } else {
                        this.replaceExistingDeploymentCountBalances(balances, resourceIds);
                    }
                    /** @see Loader.setLoaded() */
                    // @ts-expect-error TS2339
                    Loader.__vfdConstructor.setLoaded('hourCounter');
                })
                .catch((e: ApiError): void => {
                    /** @see Loader.setError() */
                    // @ts-expect-error TS2339
                    Loader.__vfdConstructor.setError('hourCounter');
                    /** @see GlobalErrorModal.globalErrorHandler() */
                    // @ts-expect-error TS2339
                    GlobalErrorModal.__vfdConstructor.globalErrorHandler(e);
                });
        }

        private loadPositionAssignmentGroupsAndPositions(): Promise<void> {
            if (!this.roster || !this.roster.rosterId) {
                return Promise.resolve();
            }

            /** @see Loader.setLoading() */
            // @ts-expect-error TS2339
            Loader.__vfdConstructor.setLoading('workSchedule');
            const endDate: Date = Dates.Utils.addDays(this.rosterViewSettings.startDate, this.rosterViewSettings.visibleDays - 1);

            return ApiClient.instance.fetchPositionAssignmentSetRosters(this.roster.rosterId, this.rosterViewSettings.startDate, endDate, [
                'set.groups.positions',
            ]).then((rosters: PositionAssignmentSetRoster[]): void => {
                /** @see Loader.setLoaded() */
                // @ts-expect-error TS2339
                Loader.__vfdConstructor.setLoaded('workSchedule');
                this.rosterPositionAssignmentGroups = rosters.map((roster: PositionAssignmentSetRoster): PositionAssignmentSetGroup[] => {
                    return roster.set?.groups ?? [];
                }).flat();
            }).catch((e: ApiError): void => {
                /** @see Loader.setError() */
                // @ts-expect-error TS2339
                Loader.__vfdConstructor.setError('workSchedule');
                /** @see GlobalErrorModal.globalErrorHandler() */
                // @ts-expect-error TS2339
                GlobalErrorModal.__vfdConstructor.globalErrorHandler(e);
            });
        }

        private replaceExistingDeploymentCountBalances(balances: DeploymentCountConfigurationBalances, resourceIds: number[]): void {
            for (const configId in balances) {
                if (!(configId in this.deploymentCountConfigurationBalances)) {
                    this.deploymentCountConfigurationBalances[configId] = {resources: {}, companies: {}};
                }
                for (const resourceId of resourceIds) {
                    if (resourceId in balances[configId].resources) {
                        this.deploymentCountConfigurationBalances[configId].resources[resourceId] = balances[configId].resources[resourceId];
                    }
                }
            }
        }

        private updateViewOptions<K extends keyof ViewOptions>(key: K, value?: ViewOptions[K]): void {
            // assume undefined means false
            if (value === undefined) {
                value = false;
            }
            this.viewOptions[key] = value;
        }

        private updatePreference(name: string, value?: LeavePeriodType | number | string | boolean): void {
            if (value === undefined) {
                this.deletePreference(name);

                return;
            }

            if (typeof value === 'boolean') {
                value = value ? 1 : 0;
            }

            if (name in this.preferences && this.preferences[name] === value) {
                // nothing to update
                return;
            }

            this.preferences[name] = value;
            ApiClient.instance
                .savePreference({
                    name,
                    value,
                })
                /** @see GlobalErrorModal.globalErrorHandler() */
                // @ts-expect-error TS2339
                .catch(GlobalErrorModal.__vfdConstructor.globalErrorHandler);
        }

        private deletePreference(name: string): void {
            if (name in this.preferences) {
                delete this.preferences[name];

                ApiClient.instance
                    .deletePreference(name)
                    /** @see GlobalErrorModal.globalErrorHandler() */
                    // @ts-expect-error TS2339
                    .catch(GlobalErrorModal.__vfdConstructor.globalErrorHandler);
            }
        }

        private changeShortcutPreferences(preferences: Record<ShortcutType, KeyControlSetting>): void {
            Object.assign(this.shortcutPreferences, preferences);
        }

        private reloadPlanbord(): void {
            this.currentRosterName = '';
            /** @see Loader.setPending() */
            // @ts-expect-error TS2339
            Loader.__vfdConstructor.setPending('roster');
            /** @see Loader.setPending() */
            // @ts-expect-error TS2339
            Loader.__vfdConstructor.setPending('hourCounter');

            let organisation: Organisation | null = null;
            for (const roster of this.viewableRosters) {
                if (roster.data.id === this.rosterViewSettings.rosterId) {
                    this.currentRosterName = roster.data.name;
                    document.title = roster.data.name + ' - ' + DEFAULT_PAGE_TITLE;

                    organisation = this.organisations[roster.data.organisationId];
                    if (!organisation) {
                        throw new Error('Cannot load roster, as organisation could not be found.');
                    }
                    break;
                }
            }

            if (organisation === null) {
                document.title = DEFAULT_PAGE_TITLE;

                this.rosterViewSettings.rosterId = 0;
                /** @see Loader.resetStates() */
                // @ts-expect-error TS2339
                Loader.__vfdConstructor.resetStates();

                return;
            }

            /** @see Loader.setLoading() */
            // @ts-expect-error TS2339
            Loader.__vfdConstructor.setLoading('roster');
            RosterLoader.load(this.rosterViewSettings, this.showPlusMinusBalanceFrom, this.companies, organisation, this.shifts)
                .then((loadedRoster: RosterData): void => {
                    this.roster = loadedRoster;
                    /** @see Loader.setLoaded() */
                    // @ts-expect-error TS2339
                    Loader.__vfdConstructor.setLoaded('roster');

                    this.loadDeploymentCountConfigurationBalances();
                    this.loadBudgetDayBalances();
                    if (loadedRoster.secondaryPage?.type === 'position-assignment') {
                        this.loadPositionAssignmentGroupsAndPositions();
                    }
                })
                .catch((e: Error): void => {
                    document.title = DEFAULT_PAGE_TITLE;
                    console.error(e.message);
                    /** @see Notify.createNotification() */
                    // @ts-expect-error TS2339
                    Notify.__vfdConstructor.createNotification({text: 'Het rooster kon niet geladen worden.', type: 'error'});
                    /** @see Loader.setError() */
                    // @ts-expect-error TS2339
                    Loader.__vfdConstructor.setError('roster');
                });

            this.loadWorkSchedulePositionsPerDay();
        }

        private loadWorkSchedulePositionsPerDay(): void {
            if (!this.canEditWorkSchedules) {
                return;
            }

            /** @see Loader.setLoading() */
            // @ts-expect-error TS2339
            Loader.__vfdConstructor.setLoading('workSchedule');
            this.workSchedulePositionsPerDay = {};
            const beginDate: Date = Dates.Utils.dateToAmsterdamMidnightDate(this.rosterViewSettings.startDate);
            const endDate: Date = Dates.Utils.endOfDay(Dates.Utils.dateToAmsterdamMidnightDate(Dates.Utils.addDays(this.rosterViewSettings.startDate, this.rosterViewSettings.visibleDays - 1)));
            ApiClient.instance.fetchWorkSchedulePositions(beginDate, endDate, this.rosterViewSettings.rosterId, ['workScheduleAssignments'], [WorkScheduleFulfillmentRequirement.REQUIRED])
                .then((positions: WorkSchedulePosition[]): void => {
                    this.storePositions(positions.map(WorkScheduleUtil.convertWorkSchedulePositionToPlanbordWorkSchedulePosition));
                    /** @see Loader.setLoaded() */
                    // @ts-expect-error TS2339
                    Loader.__vfdConstructor.setLoaded('workSchedule');
                })
                .catch((e: ApiError): void => {
                    /** @see Loader.setError() */
                    // @ts-expect-error TS2339
                    Loader.__vfdConstructor.setError('workSchedule');
                    /** @see GlobalErrorModal.globalErrorHandler() */
                    // @ts-expect-error TS2339
                    GlobalErrorModal.__vfdConstructor.globalErrorHandler(e);
                });
        }

        private storePositions(workSchedulePositions: PlanbordWorkSchedulePosition[]): void {
            const workSchedulePositionsSplitPerDay: Record<string, PlanbordWorkSchedulePosition[]> = {};
            for (const workSchedulePosition of workSchedulePositions) {
                const beginDate: string = Dates.Formatter.formatDateComputerFriendly(workSchedulePosition.beginDateTime);
                const endDate: string = Dates.Formatter.formatDateComputerFriendly(workSchedulePosition.endDateTime);

                workSchedulePositionsSplitPerDay[beginDate] ??= [];
                workSchedulePositionsSplitPerDay[beginDate].push(workSchedulePosition);

                if (endDate != beginDate) {
                    workSchedulePositionsSplitPerDay[endDate] ??= [];
                    workSchedulePositionsSplitPerDay[endDate].push(workSchedulePosition);
                }
            }

            this.workSchedulePositionsPerDay = workSchedulePositionsSplitPerDay;
        }

        private updateWorkSchedules(date: string, schedules: PlanbordWorkSchedulePosition[]): void {
            this.workSchedulePositionsPerDay[date] = schedules;
        }

        private rosterProposed(newDate: Date): void {
            if (!this.roster) {
                return;
            }

            this.roster.proposedUntil = newDate;
            for (const day of this.roster.days) {
                day.proposed = day.date <= newDate;
            }

            for (const viewableRoster of this.viewableRosters) {
                if (viewableRoster.data.id === this.roster.rosterId) {
                    viewableRoster.data.proposedUntil = newDate;
                    break;
                }
            }
        }

        private reloadResources(resourceIds: number[]): void {
            this.updateResources(resourceIds, [
                'leavePeriods',
                'balances',
                'atwIssues',
                'deployments',
                'deployments.specifications',
                'deployments.positionAssignmentSetGroupPositionDeployments',
            ]);
        }

        private reloadResource(resourceId: number): void {
            this.updateResource(resourceId, [
                'leavePeriods',
                'balances',
                'atwIssues',
                'deployments',
                'deployments.specifications',
                'deployments.positionAssignmentSetGroupPositionDeployments',
            ]);
        }

        private updateResource(resourceId: number, includes: string[]): void {
            const endDate: Date = Dates.Utils.addDays(this.rosterViewSettings.startDate, this.rosterViewSettings.visibleDays - 1);

            /** @see Loader.setLoading() */
            // @ts-expect-error TS2339
            Loader.__vfdConstructor.setLoading('resources');
            ApiClient.instance.fetchResource(
                resourceId,
                this.showPlusMinusBalanceFrom || null,
                endDate,
                this.rosterViewSettings.startDate,
                endDate,
                includes,
            )
                .then(this.saveResourceUpdates)
                .then((): void => {
                    /** @see Loader.setLoaded() */
                    // @ts-expect-error TS2339
                    Loader.__vfdConstructor.setLoaded('resources');
                })
                .catch((): void => {
                    /** @see Loader.setError() */
                    // @ts-expect-error TS2339
                    Loader.__vfdConstructor.setError('resources');
                });
        }

        private updateResources(resourceIds: number[], includes: string[]): void {
            if (resourceIds.length === 0) {
                return;
            }

            const endDate: Date = Dates.Utils.addDays(this.rosterViewSettings.startDate, this.rosterViewSettings.visibleDays - 1);

            /** @see Loader.setLoading() */
            // @ts-expect-error TS2339
            Loader.__vfdConstructor.setLoading('resources');
            ApiClient.instance.fetchResources(
                ArrayUtils.unique(resourceIds),
                this.showPlusMinusBalanceFrom || null,
                endDate,
                this.rosterViewSettings.startDate,
                endDate,
                includes,
            )
                .then((resources: Resource[]): void => {
                    for (const resource of resources) {
                        this.saveResourceUpdates(resource);
                    }
                    /** @see Loader.setLoaded() */
                    // @ts-expect-error TS2339
                    Loader.__vfdConstructor.setLoaded('resources');
                })
                .catch((): void => {
                    /** @see Loader.setError() */
                    // @ts-expect-error TS2339
                    Loader.__vfdConstructor.setError('resources');
                });
        }

        private saveResourceUpdates(updatedResource: Resource): void {
            for (const resource of this.roster!.rosterResources) {
                if (resource.id === updatedResource.id) {
                    if (updatedResource.deployments) {
                        resource.deployments = RosterConverter.keyedByDate(updatedResource.deployments);
                    }
                    if (updatedResource.balances) {
                        resource.balances = updatedResource.balances;
                    }
                    if (updatedResource.atwIssues) {
                        resource.atwIssues = updatedResource.atwIssues;
                    }
                    if (updatedResource.leavePeriods) {
                        resource.leavePeriods = updatedResource.leavePeriods.map((leavePeriod: LeavePeriod): PlanbordLeavePeriod => LeavePeriodHandler.addProblematicDays(leavePeriod, resource, this.organisation!));
                    }
                    if (updatedResource.personalStamps) {
                        resource.personalStamps = updatedResource.personalStamps;
                    }

                    return;
                }
            }
        }

        private reloadResourceStampData(resourceId: number): void {
            this.updateResource(resourceId, Planbord.INCLUDES_FOR_STAMP_RELOAD);
            this.loadDeploymentCountConfigurationBalances([resourceId]);
        }

        private reloadResourcesStampData(resourceIds: number[]): void {
            this.updateResources(resourceIds, Planbord.INCLUDES_FOR_STAMP_RELOAD);
            this.loadDeploymentCountConfigurationBalances(resourceIds);
        }

        private reloadResourceDeploymentData(resourceId: number): void {
            this.updateResource(resourceId, [
                'leavePeriods',
                'balances',
                'atwIssues',
                'deployments.specifications',
                'deployments.positionAssignmentSetGroupPositionDeployments',
            ]);
        }

        private reloadResourcesDeploymentData(resourceIds: number[]): void {
            this.updateResources(resourceIds, [
                'leavePeriods',
                'balances',
                'atwIssues',
                'deployments.specifications',
            ]);
            this.loadDeploymentCountConfigurationBalances(resourceIds);
        }

        private loadOrganisations(): Promise<void> {
            /** @see Loader.setLoading() */
            // @ts-expect-error TS2339
            Loader.__vfdConstructor.setLoading('rosters');

            return ApiClient.instance.fetchOrganisations()
                .then((loadedOrganisations: Record<string, Organisation>): void => {
                    Object.assign(this.organisations, loadedOrganisations);
                    /** @see Loader.setLoaded() */
                    // @ts-expect-error TS2339
                    Loader.__vfdConstructor.setLoaded('rosters');
                })
                .catch((e: ApiError): void => {
                    /** @see Loader.setError() */
                    // @ts-expect-error TS2339
                    Loader.__vfdConstructor.setError('rosters');
                    /** @see GlobalErrorModal.globalErrorHandler() */
                    // @ts-expect-error TS2339
                    GlobalErrorModal.__vfdConstructor.globalErrorHandler(e);
                });
        }

        private updatePlusMinusBalancesShownFrom(date: Date | false): void {
            this.showPlusMinusBalanceFrom = date;

            this.updatePreference('plusMinusHoursShownFrom', date ? date.toISOString() : undefined);

            this.updateHourBalances();
        }

        private updateDeploymentCounterFrom(from: DeploymentCounterStart): void {
            this.showDeploymentCounterFrom = from;
            const value: string | number = from.date ? from.date.toISOString() : from.days;

            this.updatePreference('deploymentCounterFrom', value);

            if (this.roster) {
                this.loadDeploymentCountConfigurationBalances();
            }
        }

        private updateEnabledBalanceFilters(balanceFilters: Record<string, BalanceFilterEnabled>): void {
            this.enabledBalanceFilters = balanceFilters;
        }

        private updateHourBalances(): void {
            if (!this.roster) {
                return;
            }

            const resourceIds: number[] = this.roster.rosterResources.map((rosterResource: ResourceWithKeyedByDateObjects): number => rosterResource.id!);

            if (resourceIds.length === 0) {
                return;
            }

            const endDate: Date = Dates.Utils.addDays(this.rosterViewSettings.startDate, this.rosterViewSettings.visibleDays - 1);
            /** @see Loader.setLoading() */
            // @ts-expect-error TS2339
            Loader.__vfdConstructor.setLoading('hourCounter');
            ApiClient.instance.fetchResources(
                ArrayUtils.unique(resourceIds),
                this.showPlusMinusBalanceFrom || null,
                endDate,
                this.rosterViewSettings.startDate,
                endDate,
                ['balances'],
            )
                .then((response: Resource[]): void => {
                    const updatedResourcesIndexedById: Record<number, Resource> = {};
                    for (const resource of response) {
                        updatedResourcesIndexedById[resource.id!] = resource;
                    }
                    for (const resource of this.roster!.rosterResources) {
                        resource.balances = updatedResourcesIndexedById[resource.id!].balances;
                    }
                    /** @see Loader.setLoaded() */
                    // @ts-expect-error TS2339
                    Loader.__vfdConstructor.setLoaded('hourCounter');
                })
                .catch((): void => {
                    console.error('Unable to update resource balances.');
                    /** @see Loader.setError() */
                    // @ts-expect-error TS2339
                    Loader.__vfdConstructor.setError('hourCounter');
                });
        }

        private doMarkersExistAtIndex(index: number): boolean {
            return this.amountOfMarkers > index && index >= 0;
        }

        private resetNavigationPosition(): void {
            this.activeHorizontalPosition = -1;
            this.activeVerticalPosition = -1;
        }

        private clickHandler(e: Event): void {
            const keyboardAutoComplete: HTMLElement | null = document.querySelector('.keyboard-deployment-autocomplete');
            if (e.target && (e.target as Element).contains(keyboardAutoComplete)) {
                /** @see PlanbordGrid.resetActiveCell() */
                // @ts-expect-error TS2339
                PlanbordGrid.__vfdConstructor.resetActiveCell();
            }
        }

        private keyboardNavigationEventHandler(e: KeyboardEvent): void {
            // If ctrl-up or ctrl-down (CMD on Mac) is pressed, emit a custom event, otherwise navigate trough roster
            if ((e.ctrlKey && e.which === 38) || (e.metaKey && e.which === 38)) {
                /** @see KeyboardDeploymentAutocomplete.navigateChoicesUp() */
                // @ts-expect-error TS2339
                KeyboardDeploymentAutocomplete.__vfdConstructor.navigateChoicesUp();
            } else if ((e.ctrlKey && e.which === 40) || (e.metaKey && e.which === 40)) {
                /** @see KeyboardDeploymentAutocomplete.navigateChoicesDown() */
                // @ts-expect-error TS2339
                KeyboardDeploymentAutocomplete.__vfdConstructor.navigateChoicesDown();
            } else if (e.key === 'Escape') {
                /** @see PlanbordGrid.deselectAllCells() */
                // @ts-expect-error TS2339
                PlanbordGrid.__vfdConstructor.deselectAllCells();
            } else {
                // Define direction
                let direction: NavigationDirection = 'Unknown';
                // Determine which key was pressed
                switch (e.key) {
                    case 'PageUp':
                    case 'PageDown':
                    case 'End':
                    case 'Home':
                        direction = e.key;
                        break;
                    case 'ArrowLeft':
                    case 'Left':
                        direction = 'Left';
                        break;
                    case 'ArrowUp':
                    case 'Up':
                        direction = 'Up';
                        break;
                    case 'ArrowDown':
                    case 'Down':
                        direction = 'Down';
                        break;
                    case 'ArrowRight':
                    case 'Right':
                        direction = 'Right';
                        break;
                }
                // Fire function to handle handleKeyboardNavigation on the board
                if (direction !== 'Unknown') {
                    // 'shift' is the key for multiselect
                    this.handleKeyboardNavigation(direction, e.shiftKey);
                    e.preventDefault();
                }
            }
        }

        private handleKeyboardNavigation(direction: NavigationDirection, isMultiSelect: boolean): void {
            // Check if a position is already active, if not, set top left active
            if (this.activeHorizontalPosition === -1) {
                this.activeHorizontalPosition = 0;
                this.activeVerticalPosition = 0;
            }

            const nextMarkerIndex: number = this.getNextMarkerIndexByDirection(direction, this.activeMarkerIndex);
            if (this.doMarkersExistAtIndex(nextMarkerIndex) && !isMultiSelect) {
                // Navigate through the markers in the cell
                this.activeMarkerIndex = nextMarkerIndex;
            } else {
                // Navigate to another cell in the grid
                this.setNewPositionsByDirection(direction);
                // If you go left you want to see the last marker in the cell, if you go right you want to see the first...
                this.setInitialMarkerIndexByDirection(direction);
            }
            if (isMultiSelect) {
                this.setMultiSelectByKeyboardDirection(direction);
            } else {
                /** @see PlanbordGrid.resetGrid() */
                // @ts-expect-error TS2339
                PlanbordGrid.__vfdConstructor.resetGrid();
            }
            this.activateCell(this.activeMarkerIndex, false);
        }

        private setMultiSelectByKeyboardDirection(direction: NavigationDirection): void {
            // Reset the active marker for so when leaving the multiselect you will start at the first marker
            this.activeMarkerIndex = 0;
            if (direction === 'Left' || direction === 'Right' || direction === 'Up' || direction === 'Down') {
                // Disable any open autocomplete dropdowns on the grid
                /** @see KeyboardDeploymentAutocomplete.disable() */
                // @ts-expect-error TS2339
                KeyboardDeploymentAutocomplete.__vfdConstructor.disable();
                /** @see PlanbordGrid.selectCell() */
                // @ts-expect-error TS2339
                PlanbordGrid.__vfdConstructor.selectCell({
                    x: this.activeHorizontalPosition,
                    y: this.activeVerticalPosition,
                });
            }
        }

        private getNextMarkerIndexByDirection(direction: NavigationDirection, markerIndex: number): number {
            switch (direction) {
                case 'Left':
                    markerIndex--;
                    break;
                case 'Right':
                    markerIndex++;
                    break;
                // In any other case stop navigating through the cell markers
                default:
                    markerIndex = -1;
            }

            return markerIndex;
        }

        private setInitialMarkerIndexByDirection(direction: NavigationDirection): void {
            switch (direction) {
                case 'Left':
                    this.activeMarkerIndex = Math.max(0, this.amountOfMarkers - 1); // remove the - 1 to select the add button (if it exists)
                    break;
                case 'Right':
                case 'Down':
                case 'Up':
                    this.activeMarkerIndex = 0;
                    break;
            }
        }

        private setNewPositionsByDirection(direction: NavigationDirection): void {
            let newHorizontalPosition: number = this.activeHorizontalPosition;
            let newVerticalPosition: number = this.activeVerticalPosition;
            switch (direction) {
                case 'PageUp':
                    newVerticalPosition = (this.doesElementExist(this.activeHorizontalPosition, 0)) ? 0 : this.activeVerticalPosition;
                    break;
                case 'PageDown':
                    newVerticalPosition = (this.doesElementExist(this.activeHorizontalPosition, (this.rosterHeight - 1))) ? this.rosterHeight - 1 : this.activeVerticalPosition;
                    break;
                case 'End':
                    newHorizontalPosition = (this.doesElementExist(this.rosterWidth - 1, this.activeVerticalPosition)) ? this.rosterWidth - 1 : this.activeHorizontalPosition;
                    break;
                case 'Home':
                    newHorizontalPosition = (this.doesElementExist(0, this.activeVerticalPosition)) ? 0 : this.activeHorizontalPosition;
                    break;
                case 'Left':
                    newHorizontalPosition = (this.doesElementExist((this.activeHorizontalPosition - 1), this.activeVerticalPosition)) ? this.activeHorizontalPosition - 1 : this.activeHorizontalPosition;
                    break;
                case 'Up':
                    newVerticalPosition = (this.doesElementExist(this.activeHorizontalPosition, (this.activeVerticalPosition - 1))) ? this.activeVerticalPosition - 1 : this.activeVerticalPosition;
                    break;
                case 'Right':
                    newHorizontalPosition = (this.doesElementExist((this.activeHorizontalPosition + 1), this.activeVerticalPosition)) ? this.activeHorizontalPosition + 1 : this.activeHorizontalPosition;
                    break;
                case 'Down':
                    newVerticalPosition = (this.doesElementExist(this.activeHorizontalPosition, (this.activeVerticalPosition + 1))) ? this.activeVerticalPosition + 1 : this.activeVerticalPosition;
                    break;
                default:
                    throw new Error('Unknown direction: ' + direction);
            }

            // Set activePosition in data property
            this.activeHorizontalPosition = newHorizontalPosition;
            this.activeVerticalPosition = newVerticalPosition;
        }

        private activateCell(index: number, openDropdown: boolean, forceAdd: boolean = false, isProposal: boolean = false): void {
            // Activate cell (visually)
            /** @see PlanbordGrid.setActiveCell() */
            // @ts-expect-error TS2339
            PlanbordGrid.__vfdConstructor.setActiveCell({
                x: this.activeHorizontalPosition,
                y: this.activeVerticalPosition,
                index,
            });

            this.scrollActiveCellIntoView(openDropdown);

            // Define closest TD (surrounding TD)
            const component: PlanbordCell | undefined = this.cell;
            // If not null, add class active
            if (!component) {
                console.error('Unable to find current cell to activate.');

                return;
            }
            // Don't continue if the current cell is selected within a multiSelect.
            if (this.isActiveSelectedCell) {
                return;
            }
            // Emit event to activate keyboard deployment autocomplete and transfer data
            const personalStamp: PersonalStamp | undefined = component.stamp;
            let personalStampShift: PersonalStampShift | undefined;

            if (personalStamp && component.stampShifts) {
                personalStampShift = !forceAdd && component.stampShifts.length > index ? component.stampShifts[index] : undefined;
            }

            /** @see KeyboardDeploymentAutocomplete.activate() */
            // @ts-expect-error TS2339
            KeyboardDeploymentAutocomplete.__vfdConstructor.activate(
                index,
                component,
                openDropdown,
                personalStamp,
                personalStampShift,
                isProposal,
            );
            // Allows the cell to be copied
            /** @see PlanbordGrid.selectCell() */
            /** @see PlanbordGrid.getActiveCell() */
            // @ts-expect-error TS2339
            PlanbordGrid.__vfdConstructor.selectCell(PlanbordGrid.__vfdConstructor.getActiveCell());
        }

        // Method to return if given element exists
        private doesElementExist(horizontalPos: number, verticalPos: number): boolean {
            /** @see PlanbordGrid.getRow() */
            // @ts-expect-error TS2339
            const row: PlanbordGridRow | undefined = PlanbordGrid.__vfdConstructor.getRow(verticalPos);

            return !!row && row.getCell(horizontalPos) !== undefined;
        }

        private createWish(wish: PersonalPlanningWish): void {
            if (!this.roster) {
                throw new Error('Roster not loaded yet.');
            }

            for (const resource of this.roster.rosterResources) {
                if (resource.id === wish.resourceId) {
                    resource.personalPlanningWishes!.push(wish);

                    return;
                }
            }
        }

        private updateWish(wish: PersonalPlanningWish): void {
            if (!this.roster) {
                throw new Error('Roster not loaded yet.');
            }

            for (const resource of this.roster.rosterResources) {
                if (resource.id === wish.resourceId) {
                    for (let k: number = 0; k < resource.personalPlanningWishes!.length; k++) {
                        if (resource.personalPlanningWishes![k].id === wish.id) {
                            resource.personalPlanningWishes![k] = wish;

                            return;
                        }
                    }
                    throw new Error('Unable to update wish, as the wish was not found.');
                }
            }
        }

        private deleteWish(wish: PersonalPlanningWish): void {
            if (!this.roster) {
                throw new Error('Roster not loaded yet.');
            }

            for (const resource of this.roster.rosterResources) {
                if (resource.id === wish.resourceId) {
                    for (let k: number = 0; k < resource.personalPlanningWishes!.length; k++) {
                        if (resource.personalPlanningWishes![k].id === wish.id) {
                            resource.personalPlanningWishes!.splice(k, 1);

                            return;
                        }
                    }
                    throw new Error('Unable to delete wish, as the wish was not found.');
                }
            }
        }

        private createSickPeriod(period: SickPeriod): void {
            if (!this.roster) {
                throw new Error('Roster not loaded yet.');
            }

            for (const resource of this.roster.rosterResources) {
                if (resource.id === period.resourceId) {
                    resource.sickPeriods!.push(period);

                    return;
                }
            }
        }

        private updateSickPeriod(period: SickPeriod): void {
            if (!this.roster) {
                throw new Error('Roster not loaded yet.');
            }

            for (const resource of this.roster.rosterResources) {
                if (resource.id === period.resourceId) {
                    for (let i: number = 0; i < resource.sickPeriods!.length; i++) {
                        if (resource.sickPeriods![i].id === period.id) {
                            resource.sickPeriods![i] = period;

                            return;
                        }
                    }
                    throw new Error('Unable to update sick period, as the period was not found.');
                }
            }
        }

        private deleteSickPeriod(period: SickPeriod): void {
            if (!this.roster) {
                throw new Error('Roster not loaded yet.');
            }

            for (const resource of this.roster.rosterResources) {
                if (resource.id === period.resourceId) {
                    for (let i: number = 0; i < resource.sickPeriods!.length; i++) {
                        if (resource.sickPeriods![i].id === period.id) {
                            resource.sickPeriods!.splice(i, 1);

                            return;
                        }
                    }
                    throw new Error('Unable to delete sick period, as the period was not found.');
                }
            }
        }

        private updateAssignedDay(assignedDay: AssignedDay): void {
            if (!this.roster) {
                throw new Error('Roster not loaded yet.');
            }

            // Item is always updated so enforcing update of entry
            for (const resource of this.roster.rosterResources) {
                if (resource.id === assignedDay.resourceId) {
                    const date: string = Dates.Formatter.formatDateISO(assignedDay.date);
                    resource.assignedDays[date] = [
                        assignedDay,
                    ];
                }
            }
        }

        private deleteAssignedDay(assignedDay: AssignedDay): void {
            if (!this.roster) {
                throw new Error('Roster not loaded yet.');
            }

            // Item is always updated so enforcing update of entry
            for (const resource of this.roster.rosterResources) {
                if (resource.id === assignedDay.resourceId) {
                    const date: string = Dates.Formatter.formatDateISO(assignedDay.date);
                    delete resource.assignedDays[date];
                }
            }
        }

        private updateStampAssignedDay(resourceId: number, assignedDay: PersonalStampAssignedDay): void {
            if (!this.roster) {
                throw new Error('Roster not loaded yet.');
            }

            // Item is always updated so enforcing update of entry
            for (const resource of this.roster.rosterResources) {
                if (resource.id === resourceId) {
                    for (const stamp of resource.personalStamps!) {
                        if (stamp.id === assignedDay.stampId) {
                            for (const existingAssignedDay of stamp.personalStampAssignedDays!) {
                                if (existingAssignedDay.dayIndex === assignedDay.dayIndex) {
                                    existingAssignedDay.id = assignedDay.id;
                                    existingAssignedDay.type = assignedDay.type;

                                    return;
                                }
                            }

                            stamp.personalStampAssignedDays!.push(assignedDay);

                            return;
                        }
                    }
                }
            }
        }

        private deleteStampAssignedDay(resourceId: number, assignedDay: PersonalStampAssignedDay): void {
            if (!this.roster) {
                throw new Error('Roster not loaded yet.');
            }

            // Item is always updated so enforcing update of entry
            for (const resource of this.roster.rosterResources) {
                if (resource.id === resourceId) {
                    for (const stamp of resource.personalStamps!) {
                        if (stamp.id === assignedDay.stampId) {
                            for (const i in stamp.personalStampAssignedDays!) {
                                if (stamp.personalStampAssignedDays![i].id === assignedDay.id) {
                                    stamp.personalStampAssignedDays.splice(Number(i), 1);

                                    return;
                                }
                            }
                        }
                    }
                }
            }
        }

        private saveRosterComment(comment: RosterCommentModel): void {
            if (!this.roster) {
                throw new Error('Cannot save comment, the roster was not loaded.');
            }

            if (comment.id) {
                const index: number = this.roster.comments!.findIndex((rosterComment: RosterCommentModel): boolean => rosterComment.id === comment.id);
                if (index !== -1) {
                    this.roster.comments![index] = comment;

                    return;
                }
            }
            this.roster.comments!.push(comment);
        }

        private deleteRosterComment(comment: RosterCommentModel): void {
            if (!this.roster) {
                throw new Error('Cannot delete comment, the roster was not loaded.');
            }

            const index: number = this.roster.comments!.findIndex((rosterComment: RosterCommentModel): boolean => rosterComment.id === comment.id);
            if (index === -1) {
                throw new Error('Cannot delete comment, it was not found in the comments.');
            }

            this.roster.comments!.splice(index, 1);
        }

        private saveEmploymentComment(employmentComment: EmploymentComment): void {
            if (!(this.roster && this.roster.rosterResources)) {
                return;
            }

            for (const resource of this.roster.rosterResources) {
                if (resource.employment && resource.employment?.id === employmentComment.employmentId) {
                    if (employmentComment.date) {
                        if (resource.employment.commentsByDate === undefined) {
                            resource.employment.commentsByDate = {};
                        }
                        const dateString: string = Dates.Formatter.formatDateComputerFriendly(employmentComment.date);
                        resource.employment.commentsByDate[dateString] = employmentComment;
                    } else {
                        resource.employment.comment = employmentComment;
                    }
                }
            }
        }

        private createDeploymentProposal(deploymentProposal: DeploymentProposal): void {
            // use a fake id (which we check to be unique), to prevent issues with clones [e.g. objects that have proxies which are not equal]
            // TODO: not sure why this isn't a simple counter
            let fakeId: number = -Date.now();
            while (fakeId in this.fakeIdPool) {
                fakeId--;
            }

            this.fakeIdPool[fakeId] = true;

            const localDeploymentProposal: DeploymentProposal = this.saveDeploymentProposalInLocalRoster({...deploymentProposal, id: fakeId});
            localDeploymentProposal.updating = true;

            ApiClient.instance.saveDeploymentProposal(deploymentProposal)
                .then((deploymentProposalId: number): void => {
                    localDeploymentProposal.updating = false;
                    localDeploymentProposal.id = deploymentProposalId;

                    // only remove the fake id if everything succeeded
                    // otherwise just accept that there'll be a few bytes in memory that may or may not be needed anymore
                    delete this.fakeIdPool[fakeId];
                })
                .catch((error: ApiError): void => {
                    let errorsShown: boolean = false;
                    if (error.response?.data) {
                        if ('salaryAccepted' in error.response.data.errors) {
                            /** @see Notify.createNotification() */
                            // @ts-expect-error TS2339
                            Notify.__vfdConstructor.createNotification({text: 'De urenverantwoording is al geaccordeerd.', type: 'error'});
                            errorsShown = true;
                        }
                        if ('unemployed' in error.response.data.errors) {
                            /** @see Notify.createNotification() */
                            // @ts-expect-error TS2339
                            Notify.__vfdConstructor.createNotification({text: 'De medewerker is niet in dienst.', type: 'error'});
                            errorsShown = true;
                        }
                    }

                    if (!errorsShown) {
                        this.handleErrors(error);
                    }
                });
        }

        private createDeployment(deployment: Deployment): void {
            // use a fake id (which we check to be unique), to prevent issues with clones [e.g. objects that have proxies which are not equal]
            // TODO: not sure why this isn't a simple counter
            let fakeId: number = -Date.now();
            while (fakeId in this.fakeIdPool) {
                fakeId--;
            }

            this.fakeIdPool[fakeId] = true;

            deployment = this.saveDeploymentInLocalRoster({...deployment, id: fakeId});
            deployment.updating = true;

            ApiClient.instance.saveNewDeployment({...deployment, companyEmployee: undefined, id: undefined})
                .then((deploymentId: number | null): void => {
                    if (deploymentId === null) {
                        this.deleteDeploymentFromLocalRoster(deployment);
                    } else {
                        deployment.updating = false;
                        deployment.id = deploymentId;
                        this.updateResourceOfDeployment(deployment);
                        this.reloadBudgetBalances();
                    }

                    this.loadWorkSchedulePositionsPerDay();

                    // only remove the fake id if everything succeeded
                    // otherwise just accept that there'll be a few bytes in memory that may or may not be needed anymore
                    delete this.fakeIdPool[fakeId];
                })
                .catch((error: ApiError): void => {
                    this.handleErrors(error);
                });
        }

        /**
         * @param deployment
         * @return Deployment -- actually a proxy object, which you can change in a reactive manner
         */
        private saveDeploymentInLocalRoster(deployment: Deployment): Deployment {
            if (this.roster === undefined) {
                throw new Error('Unable to save deployment (1).');
            }

            if (deployment.resourceId) {
                const resource: ResourceWithKeyedByDateObjects | undefined = this.allResources.find((resource: ResourceWithKeyedByDateObjects): boolean => resource.id === deployment.resourceId);
                if (resource === undefined) {
                    throw new Error('Unable to save deployment (2).');
                }

                const updatedDeployment: Deployment | false = deployment.id ? this.updateDeploymentInDictionary(resource.deployments, deployment) : false;
                if (updatedDeployment) {
                    return updatedDeployment;
                }

                const formattedDate: string = Dates.Formatter.formatDateISO(deployment.date);
                if (!(formattedDate in resource.deployments)) {
                    resource.deployments[formattedDate] = [];
                }

                resource.deployments[formattedDate].push(deployment);
                // return the object in the array, as that's a proxy object (it's not the same as just "deployment"!)
                const deployments: Deployment[] = resource.deployments[formattedDate];

                return deployments[deployments.length - 1];
            } else if (deployment.companyEmployeeId) {
                const company: CompanyWithDeploymentsAndEmployees | undefined = Object.values(this.roster.companies).find((company: CompanyWithDeploymentsAndEmployees): boolean => company.id === deployment.companyId);
                if (company === undefined) {
                    throw new Error('Unable to save deployment (3).');
                }

                const companyEmployee: CompanyEmployeeWithDeployments | undefined = Object.values(company.employees).find((employee: CompanyEmployeeWithDeployments): boolean => {
                    return employee.id === deployment.companyEmployeeId;
                });
                if (companyEmployee === undefined) {
                    throw new Error('Unable to save deployment (4).');
                }

                const updatedDeployment: Deployment | false = deployment.id ? this.updateDeploymentInDictionary(companyEmployee.deployments, deployment) : false;
                if (updatedDeployment) {
                    return updatedDeployment;
                }

                const formattedDate: string = Dates.Formatter.formatDateISO(deployment.date);
                if (!(formattedDate in companyEmployee.deployments)) {
                    companyEmployee.deployments[formattedDate] = [];
                }

                companyEmployee.deployments[formattedDate].push(deployment);
                // return the object in the array, as that's a proxy object (it's not the same as just "deployment"!)
                const deployments: Deployment[] = companyEmployee.deployments[formattedDate];

                return deployments[deployments.length - 1];
            } else if (deployment.companyId) {
                const company: CompanyWithDeploymentsAndEmployees | undefined = Object.values(this.roster.companies).find((company: CompanyWithDeploymentsAndEmployees): boolean => company.id === deployment.companyId);
                if (company === undefined) {
                    throw new Error('Unable to save deployment (5).');
                }

                const updatedDeployment: Deployment | false = deployment.id ? this.updateDeploymentInDictionary(company.deploymentsWithoutEmployee, deployment) : false;
                if (updatedDeployment) {
                    return updatedDeployment;
                }

                const formattedDate: string = Dates.Formatter.formatDateISO(deployment.date);
                if (!(formattedDate in company.deploymentsWithoutEmployee)) {
                    company.deploymentsWithoutEmployee[formattedDate] = [];
                }

                company.deploymentsWithoutEmployee[formattedDate].push(deployment);
                // return the object in the array, as that's a proxy object (it's not the same as just "deployment"!)
                const deployments: Deployment[] = company.deploymentsWithoutEmployee[formattedDate];

                return deployments[deployments.length - 1];
            }

            throw new Error('Unable to save deployment (6).');
        }

        /**
         * @param deploymentProposal
         * @return DeploymentProposal -- actually a proxy object, which you can change in a reactive manner
         */
        private saveDeploymentProposalInLocalRoster(deploymentProposal: DeploymentProposal): DeploymentProposal {
            if (this.roster === undefined) {
                throw new Error('Unable to save deployment proposal (1).');
            }

            const resource: ResourceWithKeyedByDateObjects | undefined = this.allResources.find((resource: ResourceWithKeyedByDateObjects): boolean => resource.id === deploymentProposal.resourceId);
            if (resource === undefined) {
                throw new Error('Unable to save deployment proposal (2).');
            }

            if (deploymentProposal.deploymentId) {
                // this deployment was just accepted (if deploymentId is set, you cannot update the proposal anymore, so the fact that we're updating it was the actual setting of this value)
                this.updateResourceOfDeployment(deploymentProposal);
                this.reloadBudgetBalances();
                this.loadWorkSchedulePositionsPerDay();
            }

            const updatedDeploymentProposal: DeploymentProposal | false = deploymentProposal.id ? this.updateDeploymentInDictionary(resource.deploymentProposals, deploymentProposal) : false;
            if (updatedDeploymentProposal) {
                return updatedDeploymentProposal;
            }

            const formattedDate: string = Dates.Formatter.formatDateISO(deploymentProposal.date);
            resource.deploymentProposals[formattedDate] ??= [];

            resource.deploymentProposals[formattedDate].push(deploymentProposal);
            // return the object in the array, as that's a proxy object (it's not the same as just "deployment"!)
            const deploymentProposals: DeploymentProposal[] = resource.deploymentProposals[formattedDate];

            return deploymentProposals[deploymentProposals.length - 1];
        }

        private deleteDeploymentFromLocalRoster(rosterDeployment: Deployment): void {
            const dateString: string = Dates.Formatter.formatDateISO(rosterDeployment.date);

            if (rosterDeployment.resourceId) {
                for (const resource of this.allResources) {
                    if (resource.id === rosterDeployment.resourceId && resource.deployments) {
                        const deploymentsOnDate: Deployment[] = resource.deployments[dateString] || [];
                        for (let i: number = 0; i < deploymentsOnDate.length; i++) {
                            if (resource.deployments[dateString][i].id === rosterDeployment.id) {
                                resource.deployments[dateString].splice(i, 1);

                                return;
                            }
                        }
                    }
                }
            } else if (rosterDeployment.companyId && rosterDeployment.companyEmployeeId) {
                const employeeCompanyDeployments: Deployment[] = this.roster!.companies[rosterDeployment.companyId].employees[rosterDeployment.companyEmployeeId].deployments[dateString];
                for (let i: number = 0; i < employeeCompanyDeployments.length; i++) {
                    if (employeeCompanyDeployments[i].id === rosterDeployment.id) {
                        employeeCompanyDeployments.splice(i, 1);

                        return;
                    }
                }
            } else if (rosterDeployment.companyId) {
                const companyDeployments: Deployment[] = this.roster!.companies[rosterDeployment.companyId].deploymentsWithoutEmployee[dateString];
                for (let i: number = 0; i < companyDeployments.length; i++) {
                    if (companyDeployments[i].id === rosterDeployment.id) {
                        companyDeployments.splice(i, 1);

                        return;
                    }
                }
            }
        }

        private deleteDeploymentProposalFromLocalRoster(rosterDeploymentProposal: DeploymentProposal): void {
            const dateString: string = Dates.Formatter.formatDateISO(rosterDeploymentProposal.date);

            for (const resource of this.allResources) {
                if (resource.id === rosterDeploymentProposal.resourceId) {
                    const deploymentProposalsOnDate: DeploymentProposal[] = resource.deploymentProposals[dateString] || [];
                    for (let i: number = 0; i < deploymentProposalsOnDate.length; i++) {
                        if (resource.deploymentProposals[dateString][i].id === rosterDeploymentProposal.id) {
                            resource.deploymentProposals[dateString].splice(i, 1);

                            return;
                        }
                    }
                }
            }
        }

        private deleteDeploymentsFromLocalRoster(
            resources: Record<number, ResourceWithKeyedByDateObjects>,
            companies: Record<number, Company>,
            companyEmployees: Record<number, CompanyEmployeeWithDeployments>,
            deploymentIds: number[],
        ): void {
            const deploymentMustBeKept: (deployment: Deployment) => boolean = (deployment: Deployment): boolean => deployment.id === undefined || !deploymentIds.includes(deployment.id);
            const companyEmployeeIds: number[] = Object.values(companyEmployees).map((companyEmployee: CompanyEmployeeWithDeployments): number => companyEmployee.id!);

            for (const resource of Object.values(resources)) {
                for (const date in resource.deployments) {
                    resource.deployments[date] = resource.deployments[date]!.filter(deploymentMustBeKept);
                }
            }

            for (const company of Object.values(companies)) {
                for (const date in this.roster!.companies[company.id].deploymentsWithoutEmployee) {
                    this.roster!.companies[company.id].deploymentsWithoutEmployee[date] = this.roster!.companies[company.id].deploymentsWithoutEmployee[date].filter(deploymentMustBeKept);
                }
                for (const companyEmployee of Object.values(this.roster!.companies[company.id].employees)) {
                    if (!companyEmployeeIds.includes(companyEmployee.id!)) {
                        continue;
                    }
                    for (const date in companyEmployee.deployments) {
                        this.roster!.companies[company.id].employees[companyEmployee.id!].deployments[date] = this.roster!.companies[company.id].employees[companyEmployee.id!].deployments[date].filter(deploymentMustBeKept);
                    }
                }
            }
        }

        private updateDeploymentInDictionary<T extends Deployment | DeploymentProposal>(deployments: Record<string, T[]>, newDeployment: T): T | false {
            const array: T[] = deployments[Dates.Formatter.formatDateISO(newDeployment.date)] || [];
            for (let i: number = 0; i < array.length; i++) {
                if (array[i].id === newDeployment.id) {
                    array[i] = newDeployment;

                    return array[i];
                }
            }

            return false;
        }

        private restoreDeployments(deployments: ChangedDeploymentSet): Promise<void> {
            if (deployments.save.length === 0) {
                if (deployments.remove.length) {
                    this.deleteDeploymentsAndStampShifts(
                        deployments.resource ? {[deployments.resource.id!]: deployments.resource} : {},
                        deployments.company ? {[deployments.company.id!]: deployments.company} : {},
                        deployments.remove,
                        [],
                        deployments.companyEmployee ? {[deployments.companyEmployee.id!]: deployments.companyEmployee} : {},
                    );
                }

                return Promise.resolve();
            }

            if (deployments.remove.length) {
                for (const i in deployments.remove) {
                    this.deleteDeploymentFromLocalRoster(deployments.remove[i]);
                }
            }

            return this.createOrUpdateDeployments(deployments.save, true);
        }

        private createOrUpdateDeployments(deployments: Deployment[], removeOtherDeploymentsOnSameDay: boolean = false): Promise<void> {
            for (let i: number = 0; i < deployments.length; i++) {
                if ('id' in deployments[i]) {
                    deployments[i].updating = true;
                    /** @see KeyboardDeploymentAutocomplete.updateDeployment() */
                    // @ts-expect-error TS2339
                    KeyboardDeploymentAutocomplete.__vfdConstructor.updateDeployment(deployments[i], deployments[i].id!);
                }

                deployments[i] = this.saveDeploymentInLocalRoster(deployments[i]);
            }

            return ApiClient.instance.updateDeployments(deployments, removeOtherDeploymentsOnSameDay)
                .then((ids: DeploymentBatchSaveIds): void => {
                    const resourceIds: Set<number> = new Set<number>();
                    let newDeploymentsProcessed: number = 0;
                    for (const i in deployments) {
                        if ('id' in deployments[i]) {
                            deployments[i].updating = false;
                            deployments[i].id = ids.replacements[deployments[i].id!];
                        } else {
                            deployments[i].id = ids.new[newDeploymentsProcessed++];
                        }

                        if (deployments[i].resourceId) {
                            resourceIds.add(deployments[i].resourceId!);
                        }
                    }
                    this.updateResourceIds(Array.from(resourceIds));
                    this.loadWorkSchedulePositionsPerDay();
                })
                .catch((error: ApiError): void => {
                    this.handleErrors(error);
                });
        }

        // Check if chosen shift is the default one, if so, delete current deployment
        private updateDeployment(rosterDeployment: Deployment): Promise<void> {
            rosterDeployment.updating = true;
            rosterDeployment = this.saveDeploymentInLocalRoster(rosterDeployment);

            return ApiClient.instance.updateDeployment(rosterDeployment)
                .then((newId: number | null): void => {
                    if (newId === null) {
                        this.deleteDeploymentFromLocalRoster(rosterDeployment);
                    } else {
                        rosterDeployment.updating = false;
                        rosterDeployment.id = newId;
                    }

                    this.updateResourceOfDeployment(rosterDeployment);
                    this.reloadBudgetBalances();
                    this.loadWorkSchedulePositionsPerDay();
                })
                .catch((error: ApiError): void => {
                    if (error.response?.data?.errors && 'error' in error.response.data.errors) {
                        const match: RegExpMatchArray | null = String(error.response.data.errors.error.join(',')).match(/There are no (?:enabled )?leave kinds for (\d+)/);
                        if (match) {
                            error.response.data = {
                                errors: {
                                    error: [`Er is nog geen geldige verlofregeling voor het jaar ${match[1]} voor dit type verlof`],
                                },
                            };
                        }
                    }
                    this.handleErrors(error);
                });
        }

        private updateDeploymentProposal(proposal: DeploymentProposal): Promise<void> {
            proposal.updating = true;
            proposal = this.saveDeploymentProposalInLocalRoster(proposal);

            return ApiClient.instance.saveDeploymentProposal(proposal)
                .then((id: number): void => {
                    proposal.updating = false;
                    proposal.id = id;
                })
                .catch((error: ApiError): void => {
                    this.handleErrors(error);
                });
        }

        private updateDeployments(rosterDeployments: Deployment[]): Promise<void> {
            for (const rosterDeploymentIndex in rosterDeployments) {
                const rosterDeployment: Deployment = rosterDeployments[rosterDeploymentIndex];
                rosterDeployment.updating = true;
                rosterDeployments[rosterDeploymentIndex] = this.saveDeploymentInLocalRoster(rosterDeployment);
            }

            return ApiClient.instance.updateDeployments(rosterDeployments)
                .then((batch: DeploymentBatchSaveIds): void => {
                    const resourceIds: number[] = [];
                    for (const [oldId, newId] of Object.entries(batch.replacements)) {
                        const deployment: Deployment | undefined = rosterDeployments.find((deployment: Deployment): boolean => {
                            return !!deployment.id && deployment.id === Number(oldId);
                        });

                        if (deployment) {
                            if (newId === 0) {
                                this.deleteDeploymentFromLocalRoster(deployment);
                            } else {
                                deployment.updating = false;
                                deployment.id = newId;
                            }
                            resourceIds.push(deployment.resourceId!);
                        }
                    }
                    for (const newId of batch.new) {
                        const deployment: Deployment | undefined = rosterDeployments.find((deployment: Deployment): boolean => {
                            return !deployment.id;
                        });
                        if (deployment) {
                            deployment.updating = false;
                            deployment.id = newId;
                            resourceIds.push(deployment.resourceId!);
                        }
                    }

                    this.updateResourceIds(resourceIds);
                    this.loadWorkSchedulePositionsPerDay();
                })
                .catch((error: ApiError): void => {
                    this.handleErrors(error);
                });
        }

        private deleteDeployment(rosterDeployment: Deployment): Promise<void> {
            if (!('id' in rosterDeployment)) {
                throw new Error('Cannot delete deployment without id.');
            }

            this.deleteDeploymentFromLocalRoster(rosterDeployment);

            return ApiClient.instance.deleteDeployment(rosterDeployment.id!)
                .then((): void => {
                    this.updateResourceOfDeployment(rosterDeployment);
                    this.reloadBudgetBalances();
                    this.loadWorkSchedulePositionsPerDay();
                })
                .catch((error: ApiError): void => {
                    this.handleErrors(error);
                });
        }

        private deleteDeploymentProposal(rosterDeploymentProposal: DeploymentProposal): Promise<void> {
            if (!('id' in rosterDeploymentProposal)) {
                throw new Error('Cannot delete deployment without id.');
            }

            this.deleteDeploymentProposalFromLocalRoster(rosterDeploymentProposal);

            return ApiClient.instance.deleteDeploymentProposal(rosterDeploymentProposal.id!)
                .then()
                .catch((error: ApiError): void => {
                    this.handleErrors(error);
                });
        }

        private reloadBudgetBalances(): void {
            Object.assign(this.budgetDayCounters, {});
            this.loadBudgetDayBalances();
        }

        private loadBudgetDayBalances(): Promise<void> {
            const fromDate: Date | undefined = this.rosterViewSettings?.startDate;
            if (!fromDate || this.roster === undefined || this.roster.budgets === undefined || this.roster.budgets.length < 1) {
                return Promise.resolve();
            }
            const toDate: Date = Dates.Utils.addDays(fromDate, this.rosterViewSettings!.visibleDays - 1);
            /** @see Loader.setLoading() */
            // @ts-expect-error TS2339
            Loader.__vfdConstructor.setLoading('hourCounter');

            return ApiClient.instance.fetchRosterDayDeploymentCountConfigurationBalances(this.roster!.rosterId, fromDate, toDate)
                .then((balances: RosterDayDeploymentCountConfigurationBalances): void => {
                    if (this.roster !== undefined) {
                        Object.assign(this.budgetDayCounters, BudgetDayCounterUtil.get(this.roster, balances));
                    }
                    /** @see Loader.setLoaded() */
                    // @ts-expect-error TS2339
                    Loader.__vfdConstructor.setLoaded('hourCounter');
                })
                .catch((error: ApiError): void => {
                    console.warn(error);
                    /** @see Loader.setError() */
                    // @ts-expect-error TS2339
                    Loader.__vfdConstructor.setError('hourCounter');
                });
        }

        private createStampShift(data: {shift: PersonalStampShift; resourceId: number}): Promise<void> {
            this.addStampShiftsToRoster([data.resourceId], [data.shift]);

            return ApiClient.instance.saveNewPersonalStampShift(data.shift)
                .then((stampShiftId: number): void => {
                    data.shift.id = stampShiftId;
                    this.reloadResourceStampData(data.resourceId);
                    this.reloadBudgetBalances();
                })
                .catch((error: ApiError): void => {
                    this.handleStampErrors(error);
                });
        }

        private async batchUpdateRosterRanks(data: MoveTitleRankData | MoveResourceRankData): Promise<void> {
            if (!this.roster || data.newRank == undefined) {
                return;
            }

            const rosterRankSet: RosterRankSet = RosterRankSetManager.rankSetWithUpdatedRanks(this.roster, data);

            /** @see Loader.setPending() */
            // @ts-expect-error TS2339
            Loader.__vfdConstructor.setPending('resources');

            try {
                await ApiClient.instance.batchUpdateRosterRankSetRanks(rosterRankSet);
                this.reloadRosterResourceRanksAndTitles();
                /** @see Loader.setLoaded() */
                // @ts-expect-error TS2339
                Loader.__vfdConstructor.setLoaded('resources');
            } catch (error: unknown) {
                if (error instanceof ApiError) {
                    this.handleErrors(error);
                }
                /** @see Loader.setError() */
                // @ts-expect-error TS2339
                Loader.__vfdConstructor.setError('resources');
            }
        }

        private reloadRosterResourceRanksAndTitles(): void {
            if (!this.roster) {
                return;
            }

            /** @see Loader.setLoading() */
            // @ts-expect-error TS2339
            Loader.__vfdConstructor.setLoading('resources');

            ApiClient.instance.fetchRosterRankSets(this.roster.rosterId, this.roster.days[0].date, ['titles', 'resourceRosterRanks'])
                .then((rosterRankSets: RosterRankSet[]): void => {
                    RosterRankSetManager.applyRankSetToRoster(this.roster!, rosterRankSets[0]);

                    /** @see Loader.setLoaded() */
                    // @ts-expect-error TS2339
                    Loader.__vfdConstructor.setLoaded('resources');
                })
                .catch((error: ApiError): void => {
                    this.handleErrors(error);
                    /** @see Loader.setError() */
                    // @ts-expect-error TS2339
                    Loader.__vfdConstructor.setError('resources');
                });
        }

        private updateRosterTitle(title: Title): void {
            if (this.roster && this.roster.titles) {
                const rosterTitle: Title | undefined = this.roster.titles.find((rosterTitle: Title): boolean => {
                    return rosterTitle.id === title.id;
                });

                if (rosterTitle) {
                    rosterTitle.title = title.title;
                }
            }
        }

        private deleteRosterTitle(title: Title): void {
            if (this.roster && this.roster.titles) {
                const titleIndex: number = this.roster.titles.findIndex((rosterTitle: Title): boolean => {
                    return rosterTitle.id === title.id;
                });

                if (titleIndex != -1) {
                    this.roster.titles.splice(titleIndex, 1);
                }
            }
        }

        private addStampShiftsToRoster(resourceIds: number[], personalStampShifts: PersonalStampShift[]): void {
            for (const resource of this.roster!.rosterResources) {
                if (!resource.id || !resource.personalStamps || !resourceIds.includes(resource.id)) {
                    continue;
                }
                for (const stampInResource of resource.personalStamps) {
                    if (!stampInResource.personalStampShifts) {
                        continue;
                    }

                    const stampShiftsToMutate: PersonalStampShift[]
                        = personalStampShifts.filter((shift: PersonalStampShift): boolean => shift.stampId === stampInResource.id);

                    for (const shift of stampShiftsToMutate) {
                        stampInResource.personalStampShifts.push(shift);
                    }
                }
            }
        }

        private createPersonalStampShifts(data: {shifts: PersonalStampShift[]; resourceIds: number[]}): void {
            this.addStampShiftsToRoster(data.resourceIds, data.shifts);

            ApiClient.instance.savePersonalStampShifts(data.shifts)
                .then((response: PersonalStampBatchSaveIds): void => {
                    for (const i in data.shifts) {
                        data.shifts[i].id = response.stampShiftIds[i];
                    }
                    this.reloadResourcesStampData(data.resourceIds);
                })
                .catch((error: ApiError): void => {
                    this.handleStampErrors(error);
                });
        }

        private updatePersonalStampShift(data: {shift: PersonalStampShift; resourceId: number}): void {
            data.shift.updating = true;
            this.updateStampShiftInRoster(data.resourceId, data.shift);

            ApiClient.instance.setPersonalStampShiftId(data.shift.id!, data.shift.shiftId)
                .then((): void => {
                    data.shift.updating = false;
                    this.updateStampShiftInRoster(data.resourceId, data.shift);
                    this.reloadResourceStampData(data.resourceId);
                    this.reloadBudgetBalances();
                })
                .catch((error: ApiError): void => {
                    this.handleStampErrors(error);
                });
        }

        private updateStampShiftInRoster(resourceId: number, personalStampShift: PersonalStampShift): void {
            for (const resource of this.roster!.rosterResources) {
                if (resource.id === resourceId) {
                    for (const stampInResource of resource.personalStamps!) {
                        if (stampInResource.id === personalStampShift.stampId && stampInResource.personalStampShifts) {
                            for (const key in stampInResource.personalStampShifts) {
                                const stampShiftInResource: PersonalStampShift = stampInResource.personalStampShifts[key];
                                if (stampShiftInResource.id === personalStampShift.id) {
                                    stampInResource.personalStampShifts[Number(key)] = cloneDeep(personalStampShift);
                                }
                            }
                        }
                    }
                }
            }
        }

        private deletePersonalStampShift(data: {resourceId: number; shift: PersonalStampShift}): void {
            this.deleteStampShiftsFromRoster([data.resourceId], [data.shift]);

            ApiClient.instance.deletePersonalStampShift(data.shift.id!)
                .then((): void => {
                    this.reloadResourceStampData(data.resourceId);
                    this.reloadBudgetBalances();
                })
                .catch((error: ApiError): void => {
                    this.handleStampErrors(error);
                });
        }

        private deleteStampShiftsFromRoster(resourceIds: number[], stampShifts: PersonalStampShift[]): void {
            const stampShiftIdsToRemove: number[] = stampShifts.map((shift: PersonalStampShift): number => shift.id!);

            for (const resource of this.roster!.rosterResources) {
                if (resource.id && resourceIds.includes(resource.id)) {
                    for (const stamp of resource.personalStamps!) {
                        stamp.personalStampShifts = stamp.personalStampShifts!.filter((shiftInStamp: PersonalStampShift): boolean => shiftInStamp.id === undefined || !stampShiftIdsToRemove.includes(shiftInStamp.id));
                    }
                }
            }
        }

        private deleteDeploymentsAndStampShifts(
            resources: Record<number, ResourceWithKeyedByDateObjects>,
            companies: Record<number, CompanyWithDeploymentsAndEmployees>,
            deploymentsToDelete: Deployment[],
            stampShiftsToDelete: PersonalStampShift[],
            companyEmployees: Record<number, CompanyEmployeeWithDeployments>,
        ): void {
            const promises: Promise<void>[] = [];
            const resourceIds: number[] = Object.keys(resources).map((resourceId: string): number => Number(resourceId));

            if (stampShiftsToDelete.length) {
                this.deleteStampShiftsFromRoster(resourceIds, stampShiftsToDelete);
                promises.push(ApiClient.instance.deletePersonalStampShifts(stampShiftsToDelete.map((shift: PersonalStampShift): number => shift.id!)));
            }

            if (deploymentsToDelete.length) {
                const ids: number[] = deploymentsToDelete.map((deployment: Deployment): number => deployment.id!);
                this.deleteDeploymentsFromLocalRoster(resources, companies, companyEmployees, ids);
                promises.push(ApiClient.instance.deleteDeployments(ids));
            }

            Promise.all(promises).then((): void => {
                this.reloadResources(resourceIds);
                // companies do not have balances/stamps/leave periods -> no need to reload anything
            })
                /** @see GlobalErrorModal.globalErrorHandler() */
                // @ts-expect-error TS2339
                .catch(GlobalErrorModal.__vfdConstructor.globalErrorHandler);
        }

        private deleteLeavePeriod(leavePeriod: LeavePeriod): void {
            this.deleteLeavePeriodFromLocalRoster(leavePeriod);
            this.reloadResource(leavePeriod.resourceId);
        }

        private deleteLeavePeriodFromLocalRoster(leavePeriod: LeavePeriod): void {
            for (const resource of this.roster!.rosterResources) {
                if (resource.id === leavePeriod.resourceId) {
                    if (!resource.leavePeriods) {
                        console.error('Trying to remove leave period of resource that has no leave periods. Skipping further action.');

                        return;
                    }
                    for (let i: number = 0; i < resource.leavePeriods.length; i++) {
                        if (resource.leavePeriods[i].id === leavePeriod.id) {
                            resource.leavePeriods.splice(i, 1);

                            return;
                        }
                    }
                }
            }
        }

        private handleErrors(error?: ApiError): void {
            if (!error?.response?.data) {
                this.showGenericUnknownErrorNotification();

                return;
            }

            if (error.response.data.errors) {
                for (const errorField in error.response.data.errors) {
                    for (const message of error.response.data.errors[errorField]) {
                        /** @see Notify.createNotification() */
                        // @ts-expect-error TS2339
                        Notify.__vfdConstructor.createNotification({text: message, type: 'error'});
                    }
                }
            } else {
                this.showGenericUnknownErrorNotification();
            }
        }

        private handleStampErrors(error?: ApiError): void {
            if (!error?.response) {
                this.showGenericUnknownErrorNotification();

                return;
            }

            if (error.response.rawResponse.status === HttpStatusCode.Forbidden) {
                /** @see Notify.createNotification() */
                // @ts-expect-error TS2339
                Notify.__vfdConstructor.createNotification({text: 'Deze stempel kan niet bewerkt worden. Hij is actief op minimaal één rooster waarop je geen stempelbewerkrechten hebt.', type: 'error', showDuration: 8000});

                return;
            }

            if (!error.response.data) {
                this.showGenericUnknownErrorNotification();

                return;
            }

            for (const errorField in error.response.data.errors) {
                for (const message of error.response.data.errors[errorField]) {
                    /** @see WarningInformationModal.open() */
                    // @ts-expect-error TS2339
                    WarningInformationModal.__vfdConstructor.open(message, 'Deze actie kon niet worden voltooid');
                }
            }
        }

        private updateResourceIds(resourceIds: number[]): void {
            const existingResourceIds: number[] = this.roster!.rosterResources.map((resource: PlanbordResource): number => resource.id!);
            const resourcesToUpdate: number[] = resourceIds.filter((resourceId: number): boolean => existingResourceIds.includes(resourceId));
            this.reloadResourcesDeploymentData(resourcesToUpdate);
        }

        private updateResourceOfDeployment(deployment: Deployment | DeploymentProposal): void {
            if (deployment.resourceId && this.roster!.rosterResources.some((resource: PlanbordResource): boolean => resource.id === deployment.resourceId)) {
                this.reloadResourceDeploymentData(deployment.resourceId);
                this.loadDeploymentCountConfigurationBalances([deployment.resourceId]);
            }
        }

        private openWorkSchedule(day: RosterDay): void {
            /** @see WorkSchedule.openModal() */
            // @ts-expect-error TS2339
            WorkSchedule.__vfdConstructor.openModal(day);
        }

        private openCompanyEmployeeSelectModal(company: CompanyWithDeploymentsAndEmployees): void {
            /** @see CompanyEmployeeSelectModal.open() */
            // @ts-expect-error TS2339
            CompanyEmployeeSelectModal.__vfdConstructor.open(company);
        }

        public addEmployeeToCompany(company: CompanyWithDeploymentsAndEmployees, companyEmployee: CompanyEmployeeWithDeployments): void {
            if (!this.roster) {
                return;
            }
            this.roster.companies[company.id!] ??= company;
            this.roster.companies[company.id!].employees[companyEmployee.id!] = companyEmployee;
        }

        private showGenericUnknownErrorNotification(): void {
            /** @see Notify.createNotification() */
            // @ts-expect-error TS2339
            Notify.__vfdConstructor.createNotification({text: 'Onbekende fout.', type: 'error'});
        }

        public static setActiveCell(
            horizontalPosition: number,
            verticalPosition: number,
            openDropdown: boolean,
            forceAdd: boolean = false,
            index: number = 0,
            isProposal: boolean = false,
        ): void {
            this.instance!.setActiveCell(horizontalPosition, verticalPosition, openDropdown, forceAdd, index, isProposal);
        }

        public static scrollActiveCellIntoView(dropdownIsBeingOpened: boolean): void {
            this.instance!.scrollActiveCellIntoView(dropdownIsBeingOpened);
        }
    }
</script>

<style lang="scss">
    // ! NOT SCOPED ! //
    @use '@/styles/planbord';
    @use '@/styles/deployments-holder';
    @use '@/styles/stamp-holder';
    @use '@/styles/themes/light-theme';
    @use '@/styles/themes/dark-theme';

    // Cquential SASS base library (variables, mixins, themes, icons, fonts, etc.)
    @use '@cquential/ui/common/lib-common' with (
        $cqui-assets-file-path: '@cquential/ui/common/assets/files',
    );
    @use '@cquential/ui/common/lib-external';
    @use "@cquential/ui/common/assets/typography";
    @import '@cquential/ui/style.css';

    body {
        --optionsrow-height: 4.4em;
        --filterrow-height: 3em;
        --app-footer-hidden-width: 0.35rem;
        background: var(--main-background-color);
        font-size: 13px;
        -webkit-font-smoothing: antialiased;
        -moz-osx-font-smoothing: grayscale;
        color: var(--theme-text-color);

        @include light-theme.colors;

        @media (prefers-color-scheme: dark) {
            @include dark-theme.colors;
        }

        &[data-theme="light"] {
            @include light-theme.colors;
        }

        &[data-theme="dark"] {
            @include dark-theme.colors;
        }
    }

    #planbord {
        display: grid;
        grid-template-rows: calc(var(--optionsrow-height) + 35px) calc(var(--filterrow-height) + 15px) calc(100vh - var(--optionsrow-height) - var(--filterrow-height) - 50px);
        transition: all 0.5s ease;
    }
</style>
